/* eslint sort-keys:["warn", "asc", { "minKeys": 3 }] */

import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import type { GameStatus } from '__generated__/globalTypes';
import {
  ReferralState,
  SeasonEligibility,
  ShopItemType,
} from '__generated__/globalTypes';
import type {
  AlgoliaCardIndexesName,
  GallerySortName,
  PrintableI18nPlayerSortName,
} from 'contexts/config';

export const CAPTAIN = 'C';

export const glossary = defineMessages({
  accept: {
    id: 'DirectOfferStatus.accept',
    defaultMessage: 'Accept',
  },
  accountEmail: {
    id: 'Settings.updateEmail.newTitle',
    defaultMessage: 'Account Email',
  },
  add: {
    id: 'SwapHeader.switch.add',
    defaultMessage: 'Add',
  },
  addATeam: {
    id: 'pages.play.pro.fixture.competition.add.addATeam',
    defaultMessage: 'Add a team',
  },
  addPhoneNumber: {
    id: 'accountSecurityCheck.phoneNumber.add',
    defaultMessage: 'Add phone number',
  },
  all: {
    id: 'Glossary.all',
    defaultMessage: 'All',
  },
  amateur: { id: 'Glossary.Amateur', defaultMessage: 'Amateur' },
  applyFilters: {
    id: 'glossary.applyFilters',
    defaultMessage: 'Apply filters',
  },
  auction: {
    id: 'FiatTransactionsHistoryAuction.title',
    defaultMessage: 'Auction',
  },
  auctionIdle: {
    defaultMessage: 'Idle',
    description: 'Auction status when the user has not bid on it',
    id: 'glossary.auctionIdle',
  },
  auctionOutbid: {
    id: 'OutbidLabel.label',
    defaultMessage: 'Outbid',
  },
  auctionWinning: {
    id: 'WinningLabel.label',
    defaultMessage: 'Winning',
  },
  avatar: {
    id: 'Glossary.avatar',
    defaultMessage: 'Avatar',
  },
  back: {
    id: 'DialogOnboarding.Back',
    defaultMessage: 'Back',
  },
  beginner: {
    id: 'Glossary.beginner',
    defaultMessage: 'Beginner',
  },
  bestGame: {
    id: 'chart.bestGame',
    defaultMessage: 'Best game',
  },
  beta: {
    id: 'Glossary.beta',
    defaultMessage: 'Beta',
  },
  bids: {
    id: 'Glossary.bids',
    defaultMessage: 'Bids',
  },
  biweekly: {
    id: 'Glossary.biweekly',
    defaultMessage: 'Biweekly',
  },
  boxScore: {
    id: 'Glossary.boxScore',
    defaultMessage: 'Box Score',
  },
  buy: {
    id: 'Glossary.buy',
    defaultMessage: 'Buy',
  },
  buyLowest: {
    id: 'BuyField.buyLowest',
    defaultMessage: 'Buy lowest price',
  },
  buyNow: {
    id: 'BuyField.buy',
    defaultMessage: 'Buy now',
  },
  buyWithName: {
    id: 'FootballBasicMarketCard.buyName',
    defaultMessage: 'Buy {name}',
  },
  cancel: {
    id: 'Settings.PhoneNumberVerification.EnterVerificationCode.cancel',
    defaultMessage: 'Cancel',
  },
  canceled: {
    id: 'Glossary.canceled',
    defaultMessage: 'Canceled',
  },
  card: {
    id: 'Glossary.card',
    defaultMessage: 'Card',
  },
  cards: {
    id: 'Club.Data.cards',
    defaultMessage: 'Cards',
  },
  cash: {
    id: 'Glossary.cash',
    defaultMessage: 'Cash',
  },
  cashRewards: {
    id: 'nba.landing.EarlyAccess.Win.cash',
    defaultMessage: 'Cash Rewards',
  },
  caution: {
    id: 'Glossary.caution',
    defaultMessage: 'Caution',
  },
  challenges: {
    id: 'Glossary.challenges',
    defaultMessage: 'Challenges',
  },
  championshipChase: {
    id: 'championshipChase.title',
    defaultMessage: 'Championship Chase',
  },
  chooseYourSport: {
    id: 'Landing.NewOtherSports.title',
    defaultMessage: 'Choose your sport',
  },
  claim: { id: 'Glossary.Claim', defaultMessage: 'Claim' },
  claimMyReward: {
    id: 'ComposeTeamOnboarding.promoOnboarding.claimReward',
    defaultMessage: 'Claim my Reward',
  },
  claimNow: { id: 'Glossary.ClaimNow', defaultMessage: 'Claim Now' },
  claimed: { id: 'Glossary.Claimed', defaultMessage: 'Claimed' },
  close: { id: 'Button.Close', defaultMessage: 'Close' },
  closed: {
    id: 'Lobby.CompetitionList.NoCardEntry.closed',
    defaultMessage: 'Closed',
  },
  closesIn: {
    id: 'Lobby.CompetitionList.NoCardEntry.nextClose',
    defaultMessage: 'Closes in {time}',
  },
  clubs: {
    id: 'LicensedClubs.FootballTabs.clubs',
    defaultMessage: 'Clubs',
  },
  coins: {
    id: 'Glossary.coins',
    defaultMessage: 'Coins',
  },
  collect: {
    id: 'ForYou.FirstTime.block.title.1',
    defaultMessage: 'Collect',
  },
  collectStar: {
    id: 'Glossary.collectStar',
    defaultMessage: 'Collect Star Card',
  },
  comingSoon: {
    id: 'Glossary.comingSoon',
    defaultMessage: 'Coming soon',
  },
  commonEssence: {
    id: 'Glossary.commonEssence',
    defaultMessage: 'Common Essence',
  },
  commonMarket: {
    id: 'ForYou.CommonMarket.title',
    defaultMessage: 'Common Market',
  },
  competition: {
    id: 'Glossary.competition',
    defaultMessage: 'Competition',
  },
  completed: {
    id: 'Glossary.completed',
    defaultMessage: 'Completed',
  },
  confirm: {
    id: 'ConfirmationDialogContent.confirm',
    defaultMessage: 'Confirm',
  },
  connectWallet: {
    id: 'glossary.connectWallet',
    defaultMessage: 'Connect Wallet',
  },
  continue: {
    id: 'UsedCardsDialog.continue',
    defaultMessage: 'Continue',
  },
  coopLeagues: {
    id: 'Glossary.coopLeagues',
    defaultMessage: 'Co-op Leagues',
  },
  copiedToClipboard: {
    id: 'Glossary.copiedToClipboard',
    defaultMessage: 'Copied to clipboard!',
  },
  copy: { id: 'Glossary.copy', defaultMessage: 'Copy' },
  copyLink: { id: 'Glossary.copyLink', defaultMessage: 'Copy link' },
  cost: {
    id: 'Glossary.cost',
    defaultMessage: 'Cost',
  },
  countdown: {
    id: 'OffseasonHeader.countdown',
    defaultMessage:
      '<segment>{days}<unit>d</unit></segment> : <segment>{hours}<unit>h</unit></segment> : <segment>{minutes}<unit>m</unit></segment> : <segment>{seconds}<unit>s</unit></segment>',
  },
  countdownWithoutDays: {
    id: 'OffseasonHeader.countdownWithoutDays',
    defaultMessage:
      '<segment>{hours}<unit>h</unit></segment> : <segment>{minutes}<unit>m</unit></segment> : <segment>{seconds}<unit>s</unit></segment>',
  },
  countryOfResidence: {
    id: 'Glossary.countryOfResidence',
    defaultMessage: 'Country of residence',
  },
  create: {
    id: 'DeckForm.create',
    defaultMessage: 'Create',
  },
  createYourTeam: {
    id: 'Glossary.createYourTeam',
    defaultMessage: 'Create your team',
  },
  created: {
    id: 'Glossary.created',
    defaultMessage: 'Created {date}',
  },
  crypto: {
    id: 'Glossary.crypto',
    defaultMessage: 'Crypto',
  },
  customLeagues: {
    id: 'Glossary.customLeagues',
    defaultMessage: 'Custom Leagues',
  },
  daily: {
    id: 'Home.ForYouBlock.daily',
    defaultMessage: 'Daily',
  },
  dailyDeals: {
    id: 'commonCard.BuyPacks.leaguePacks',
    defaultMessage: 'Daily Deals',
  },
  dailyHoops: {
    id: 'Glossary.daylyHoops',
    defaultMessage: 'Daily Hoops',
  },
  date: {
    id: 'Glossary.date',
    defaultMessage: 'Date',
  },
  dateOfBirth: {
    id: 'Glossary.dateOfBirth',
    defaultMessage: 'Date of birth',
  },
  decline: {
    id: 'Glossary.decline',
    defaultMessage: 'Decline',
  },
  declined: {
    id: 'Glossary.declined',
    defaultMessage: 'Declined',
  },
  delete: {
    id: 'Lobby.Upcoming.Delete',
    defaultMessage: 'Delete',
  },
  deposit: {
    id: 'TransactionsHistoryDeposit.title',
    defaultMessage: 'Deposit',
  },
  depositAction: {
    id: 'DepositEthForm.submit',
    defaultMessage: 'Deposit',
  },
  determinative: {
    id: 'Glossary.determinative',
    defaultMessage: 'a {type}',
  },
  determinativeBox: {
    id: 'Glossary.box.determinative',
    defaultMessage: 'a Box',
  },
  determinativeCard: {
    id: 'Glossary.card.determinative',
    defaultMessage: 'a Card',
  },
  determinativeCardPack: {
    id: 'Glossary.cardPack.determinative',
    defaultMessage: 'a Card Pack',
  },
  determinativeEssence: {
    id: 'Glossary.essence.determinative',
    defaultMessage: 'some Essence',
  },
  determinativeExperiences: {
    id: 'Glossary.experiences.determinative',
    defaultMessage: 'an Experience',
  },
  determinativeMarketCredit: {
    id: 'Glossary.marketCredit.determinative',
    defaultMessage: 'some Market Credit',
  },
  determinativeMoney: {
    id: 'Glossary.money.determinative',
    defaultMessage: 'some Money',
  },
  disabled: {
    id: 'Settings.settingsSection.disabled',
    defaultMessage: 'Disabled',
  },
  discount: {
    id: 'Glossary.discount',
    defaultMessage: 'Discount',
  },
  discover: {
    id: 'glossary.discover',
    defaultMessage: 'Discover',
  },
  dismiss: {
    id: 'Glossary.dismiss',
    defaultMessage: 'Dismiss',
  },
  doItLater: {
    id: 'Glossary.doItLater',
    defaultMessage: 'I’ll do it later',
  },
  done: {
    id: 'Settings.recoveryCodesDialog.button',
    defaultMessage: 'Done',
  },
  download: { id: 'Glossary.download', defaultMessage: 'Download' },
  duration: { id: 'Glossary.duration', defaultMessage: 'Duration' },
  earlyAccess: {
    id: 'Glossary.earlyAccess',
    defaultMessage: 'Early access',
  },
  edit: {
    id: 'CardCollections.CollectionFulfilledSlot.EditButton',
    defaultMessage: 'Edit',
  },
  enabled: {
    id: 'Settings.settingsSection.enabled',
    defaultMessage: 'Enabled',
  },
  endsInTime: {
    id: 'AuctionState.state.endsIn',
    defaultMessage: 'Ends in {timeRemaining}',
  },
  energy: {
    id: 'Glossary.energy',
    defaultMessage: 'Energy',
  },
  entries: {
    id: 'Glossary.entries',
    defaultMessage: 'Entries',
  },
  equipped: { id: 'ClubShop.Item.Equipped', defaultMessage: 'Equipped' },
  essence: {
    id: 'Glossary.essence',
    defaultMessage: 'Essence',
  },
  essenceLabel: {
    id: 'Glossary.essenceLabel',
    defaultMessage:
      'Essence {quantity, plural, one {capsule} other {capsules}}',
  },
  essenceRarityAmountLabel: {
    id: 'Glossary.essenceRarityAmountLabel',
    defaultMessage: '{amount} {rarity} Essence',
  },
  essenceRarityLabel: {
    id: 'Glossary.essenceRarityLabel',
    defaultMessage:
      '{rarity} Essence {quantity, plural, one {capsule} other {capsules}}',
  },
  europeanChampionship: {
    id: 'Glossary.europeanChampionship',
    defaultMessage: 'European Championship',
  },
  event: { id: 'Glossary.Event', defaultMessage: 'Event' },
  events: { id: 'Glossary.Events', defaultMessage: 'Events' },
  exclusive: { id: 'Glossary.Exclusive', defaultMessage: 'Exclusive' },
  experiences: { id: 'Glossary.Experiences', defaultMessage: 'Experiences' },
  expiration: {
    id: 'RefereeItemContent.expiration',
    defaultMessage: 'Expiration',
  },
  expired: {
    id: 'Glossary.expired',
    defaultMessage: 'Expired',
  },
  explore: {
    id: 'Glossary.explore',
    defaultMessage: 'Explore',
  },
  failed: {
    id: 'Glossary.failed',
    defaultMessage: 'Failed',
  },
  faq: {
    id: 'loan.FAQ.title',
    defaultMessage: 'Frequently asked questions',
  },
  favorites: {
    id: 'Glossary.favorites',
    defaultMessage: 'Favorites',
  },
  fewMoments: { id: 'Glossary.fewMoments', defaultMessage: 'a few moments' },
  filterBy: {
    id: 'ShopItemPicker.FilterBy.label',
    defaultMessage: 'Filter by',
  },
  find: {
    id: 'glossary.find',
    defaultMessage: 'Find',
  },
  finish: {
    id: 'glossary.finish',
    defaultMessage: 'Finish',
  },
  firstName: {
    id: 'Glossary.firstName',
    defaultMessage: 'First name',
  },
  follow: {
    id: 'Follower.add',
    defaultMessage: 'Follow',
  },
  followed: {
    id: 'Follower.followed',
    defaultMessage: 'Followed',
  },
  followers: { id: 'Follows.followers', defaultMessage: 'Followers' },
  following: { id: 'Follows.following', defaultMessage: 'Following' },
  forYou: { id: 'Glossary.forYou', defaultMessage: 'For You' },
  free: { id: 'Glossary.free', defaultMessage: 'Free' },
  freeClaimAvailable: {
    id: 'DailyClaimSection.freeClaimAvailable',
    defaultMessage: 'Free claim available',
  },
  freeClaimIn: {
    id: 'DailyClaimSection.freeClaimIn',
    defaultMessage: 'Free claim in {value}',
  },
  gameWeek: {
    id: 'missions.label.gameWeek',
    defaultMessage: 'Game Week',
  },
  games: { id: 'Glossary.games', defaultMessage: 'Games' },
  gems: {
    id: 'Glossary.gems',
    defaultMessage: 'Gems',
  },
  general: {
    id: 'Glossary.general',
    defaultMessage: 'General',
  },
  getStarted: {
    id: 'Draft.Action.GetStarted',
    defaultMessage: 'Get Started',
  },
  gif: {
    id: 'Glossary.gif',
    defaultMessage: 'GIF',
  },
  giftCard: { id: 'Glossary.Gift Card', defaultMessage: 'Gift Card' },
  global: {
    id: 'glossary.global',
    defaultMessage: 'Global',
  },
  goPro: {
    id: 'UnlockPro.unlock',
    defaultMessage: 'Go Pro',
  },
  goToMarket: {
    id: 'Inventory.RevealableCards.goToMarket',
    defaultMessage: 'Go to market',
  },
  goToMyCards: {
    id: 'ClubShop.ItemPreviewDialog.Cta.RedirectToMyCards',
    defaultMessage: 'Go to My Cards',
  },
  gotIt: {
    id: 'Glossary.gotIt',
    defaultMessage: 'Got it',
  },
  gw: {
    id: 'Glossary.gw',
    defaultMessage: 'GW',
  },
  howItWorks: {
    id: 'Glossary.howItWorks',
    defaultMessage: 'How it works',
  },
  howToPlay: {
    id: 'Glossary.howToPlay',
    defaultMessage: 'How to Play',
  },
  introducing: {
    id: 'new-season-dialog.introducing',
    defaultMessage: 'Introducing',
  },
  inventory: {
    id: 'ClubShop.Tabs.Inventory',
    defaultMessage: 'Inventory',
  },
  jerseys: { id: 'Glossary.Jerseys', defaultMessage: 'Jerseys' },
  join: {
    id: 'Glossary.join',
    defaultMessage: 'Join',
  },
  joinForFree: {
    id: 'Glossary.joinForFree',
    defaultMessage: 'Join for free',
  },
  keep: {
    id: 'Glossary.keep',
    defaultMessage: 'Keep',
  },
  landingHero: {
    id: 'landing.WalletLanding.hero',
    defaultMessage: 'Scout. Play. Win.',
  },
  landingHeroWallet: {
    id: 'landing.WalletLandingHero.walletTitle',
    defaultMessage: 'Your Name. Your Legend.',
  },
  landingHeroWalletPlaceholder: {
    id: 'landing.WalletLandingHero.walletPlaceholder',
    defaultMessage: 'Your Manager name',
  },
  lastName: {
    id: 'Glossary.lastName',
    defaultMessage: 'Last name',
  },
  later: {
    id: 'Glossary.later',
    defaultMessage: 'Later',
  },
  latest: {
    id: 'Blog.latest',
    defaultMessage: 'Latest',
  },
  latestBids: {
    id: 'PlayPage.latestBids',
    defaultMessage: 'Latest Bids',
  },
  latestNews: {
    id: 'PlayPage.blogTitle',
    defaultMessage: 'Latest News',
  },
  leaderboards: {
    id: 'Glossary.leaderboards',
    defaultMessage: 'Leaderboards',
  },
  learnMore: {
    id: 'Glossary.learnMore',
    defaultMessage: 'Learn more',
  },
  less: { id: 'Glossary.Less', defaultMessage: 'Less' },
  letsGo: {
    id: 'WelcomeToTheArena.finishOnboardingCta',
    defaultMessage: "Let's go!",
  },
  lineups: {
    id: 'Glossary.lineups',
    defaultMessage: 'Lineups',
  },
  listings: {
    id: 'StartingPriceCell.listings',
    defaultMessage: 'Listings',
  },
  loadMore: {
    id: 'Nav.loadMore',
    defaultMessage: 'Load more',
  },
  loading: {
    id: 'LoadingIndicator.loading',
    defaultMessage: 'Loading',
  },
  locked: {
    id: 'Glossary.locked',
    defaultMessage: 'Locked',
  },
  logOut: {
    id: 'Settings.logOutAllDevices.submit',
    defaultMessage: 'Log Out',
  },
  lowestPrice: {
    id: 'marketplace.commonPlayerHeaderTable.sortByPrice',
    defaultMessage: 'Lowest price',
  },
  manager: {
    id: 'Glossary.manager',
    defaultMessage: 'Manager',
  },
  matchInfo: {
    id: 'Glossary.matchInfo',
    defaultMessage: 'Match info',
  },
  matchTickets: {
    id: 'TutorialPage.ticket',
    defaultMessage: 'Match Tickets',
  },
  matches: { id: 'Glossary.matches', defaultMessage: 'Matches' },
  max: {
    id: 'BreakdownBlock.max',
    defaultMessage: 'Max',
  },
  merch: {
    id: 'Glossary.merch',
    defaultMessage: 'Merch',
  },
  modify: {
    id: 'Glossary.modify',
    defaultMessage: 'Modify',
  },
  money: {
    id: 'Glossary.money',
    defaultMessage: 'Money',
  },
  more: { id: 'Glossary.More', defaultMessage: 'More' },
  mp: {
    id: 'Challenges.Category.Task.mp',
    defaultMessage: 'MP',
  },
  mvp: {
    id: 'Glossary.mvp',
    defaultMessage: 'MVP',
  },
  myCircle: {
    id: 'MyCirclesResult.title',
    defaultMessage: 'My Circle',
  },
  myCollections: {
    id: 'Collections.title',
    defaultMessage: 'My Collections',
  },
  mySearches: {
    id: 'Glossary.mySearches',
    defaultMessage: 'My searches',
  },
  name: {
    id: 'Glossary.name',
    defaultMessage: 'Name',
  },
  nationality: {
    id: 'Glossary.nationality',
    defaultMessage: 'Nationality',
  },
  new: { id: 'Glossary.New', defaultMessage: 'New' },
  next: {
    id: 'CreateClub.confirm',
    defaultMessage: 'Next',
  },
  no: {
    id: 'WithdrawCancelDialog.cancelCta',
    defaultMessage: 'No',
  },
  noCardEntry: {
    id: 'Lobby.CompetitionList.NoCardEntry.title',
    defaultMessage: 'No Card Entry',
  },
  noGame: {
    id: 'glossary.noGame',
    defaultMessage: 'No game',
  },
  noOdds: {
    id: 'BoxRewardDialog.no_odds',
    defaultMessage: 'Odds will be shown after reveal date',
  },
  noResultsFound: {
    id: 'glossary.noResultsFound',
    defaultMessage: 'No results found',
  },
  noTeamForThisWeek: {
    id: 'baseball.lobby.myTeams.description',
    defaultMessage: 'No team registered for this week.',
  },
  noThanks: {
    id: 'Glossary.noThanks',
    defaultMessage: 'No, thanks',
  },
  notMajorError: {
    id: 'Glossary.notMajorError',
    defaultMessage: 'You must be over 18.',
  },
  now: {
    id: 'glossary.now',
    defaultMessage: 'now',
  },
  nowLive: {
    id: 'CardsLaunchBanner.nowLive',
    defaultMessage: 'Now live',
  },
  odds: {
    id: 'BoxRewardDialog.odd',
    defaultMessage: 'odds',
  },
  offSeason: {
    id: 'SelectCompetition.offSeason',
    defaultMessage: 'Off-season',
  },
  officiallyLicensed: {
    id: 'LeagueName.officiallyLicensed',
    defaultMessage: 'Officially licensed product of <b>{displayName}</b>',
  },
  officiallyLicensedProduct: {
    id: 'OfficialProduct.subtitle',
    defaultMessage: 'Officially licensed product',
  },
  ok: {
    id: 'AuctionNotification.ok',
    defaultMessage: 'OK',
  },
  onboarding: {
    id: 'glossary.onboarding',
    defaultMessage: 'Onboarding',
  },
  open: {
    id: 'Glossary.open',
    defaultMessage: 'Open',
  },
  opensIn: {
    id: 'Lobby.CompetitionList.NoCardEntry.nextOpen',
    defaultMessage: 'Opens in {time}',
  },
  or: {
    id: 'Glossary.or',
    defaultMessage: 'or',
  },
  ordinal: {
    id: 'Glossary.ordinal',
    defaultMessage:
      '{ordinal, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}',
  },
  ordinalStyled: {
    id: 'Glossary.ordinalStyled',
    defaultMessage:
      '{ordinal, selectordinal, one {#<sup>st</sup>} two {#<sup>nd</sup>} few {#<sup>rd</sup>} other {#<sup>th</sup>}}',
  },
  owned: { id: 'Glossary.owned', defaultMessage: 'Owned' },
  payWith: {
    id: 'Glossary.payWith',
    defaultMessage: 'Pay with',
  },
  percent: {
    id: 'Glossary.percent',
    defaultMessage: '{percent, number, percent}',
  },
  percentWithSup: {
    id: 'Glossary.percentWithSup',
    defaultMessage: '{percent}<sup>%</sup>',
  },
  play: {
    id: 'Glossary.play',
    defaultMessage: 'Play',
  },
  playInPro: {
    id: 'glossary.playInPro',
    defaultMessage: 'Play in Pro!',
  },
  playNow: {
    id: 'Glossary.playNow',
    defaultMessage: 'Play now',
  },
  players: {
    id: 'Glossary.players',
    defaultMessage: 'Players',
  },
  points: {
    id: 'Glossary.points',
    defaultMessage: 'Points',
  },
  poweredBy: {
    id: 'Seo.Player.News.poweredBy',
    defaultMessage: 'Powered by',
  },
  previous: {
    id: 'Glossary.previous',
    defaultMessage: 'Previous',
  },
  primaryBuy: {
    id: 'primaryBuy.buy',
    defaultMessage: 'Buy Now – {amount}',
  },
  private: {
    id: 'DeckEditorDialog.private',
    defaultMessage: 'Private',
  },
  pro: { id: 'Glossary.Pro', defaultMessage: 'Pro' },
  productUpdate: {
    id: 'glossary.productUpdate',
    defaultMessage: 'Product Update',
  },
  projection: {
    id: 'GameWeekProjection.projection',
    defaultMessage: 'Projection',
  },
  projectionShort: {
    id: 'Glossary.projectionShort',
    defaultMessage: 'Proj.',
  },
  projections: {
    id: 'GameWeekProjection.projections',
    defaultMessage: 'Projections',
  },
  public: {
    id: 'DeckEditorDialog.public',
    defaultMessage: 'Public',
  },
  quickPlay: {
    id: 'Glossary.quickPlay',
    defaultMessage: 'Quick Play',
  },
  quietWeek: {
    id: 'Lobby.Teams.Empty.title',
    defaultMessage: 'Quiet week',
  },
  ranking: { id: 'Glossary.ranking', defaultMessage: 'Ranking' },
  ready: { id: 'Glossary.ready', defaultMessage: 'Ready' },
  receive: { id: 'Glossary.receive', defaultMessage: 'Receive' },
  recoveryEmail: {
    id: 'Glossary.recoveryEmail',
    defaultMessage: 'Recovery email',
  },
  referral: {
    id: 'PlayPage.referral',
    defaultMessage: 'Referral',
  },
  register: { id: 'Glossary.Register', defaultMessage: 'Register' },
  reject: {
    id: 'glossary.reject',
    defaultMessage: 'Reject',
  },
  relatedClubs: {
    id: 'RelatedClubs.Title',
    defaultMessage: 'Related clubs',
  },
  relatedPlayers: {
    id: 'RelatedPlayers.Title',
    defaultMessage: 'Related players',
  },
  reload: { id: 'Glossary.reload', defaultMessage: 'Reload' },
  remove: { id: 'Draft.PlayerList.Cta.Remove', defaultMessage: 'Remove' },
  resendVerificationCode: {
    id: 'Glossary.resendVerificationEmail',
    defaultMessage: 'Resend verification email',
  },
  reward: {
    id: 'TransactionsHistoryReward.title',
    defaultMessage: 'Reward',
  },
  rewards: {
    id: 'Glossary.rewards',
    defaultMessage: 'Rewards',
  },
  rewardsPool: {
    id: 'Glossary.rewardsPool',
    defaultMessage: 'Rewards Pool',
  },
  santa: {
    id: 'Glossary.santa',
    defaultMessage: 'Santa',
  },
  save: {
    id: 'Glossary.save',
    defaultMessage: 'Save',
  },
  savePreferences: {
    id: 'Glossary.savePreferences',
    defaultMessage: 'Save preferences',
  },
  saved: {
    id: 'Glossary.saved',
    defaultMessage: 'Saved',
  },
  savedSearch: {
    id: 'Glossary.savedSearch',
    defaultMessage: 'Saved',
  },
  scoutAll: {
    id: 'MarketHomePage.BestPredictions.scoutAll',
    defaultMessage: 'Scout All',
  },
  search: {
    id: 'RefineList.searchPlaceholder',
    defaultMessage: 'Search',
  },
  searchByPlayerClubLeague: {
    id: 'ComposeTeam.BenchFilter.search',
    defaultMessage: 'Search by Player, Club or League',
  },
  seasonal: {
    id: 'Glossary.seasonal',
    defaultMessage: 'Seasonal',
  },
  seeAll: { id: 'Glossary.SeeAll', defaultMessage: 'See all' },
  seeDetails: { id: 'Glossary.SeeDetails', defaultMessage: 'See details' },
  seeTotalPrizePool: {
    id: 'Glossary.SeeTotalPrizePool',
    defaultMessage: 'See total prize pool',
  },
  select: {
    id: 'Glossary.Select',
    defaultMessage: 'Select',
  },
  send: { id: 'Glossary.send', defaultMessage: 'Send' },
  share: { id: 'Glossary.share', defaultMessage: 'Share' },
  showLess: {
    id: 'Glossary.showLess',
    defaultMessage: 'Show less',
  },
  showMore: {
    id: 'Glossary.showMore',
    defaultMessage: 'Show more',
  },
  showPlayerInfo: {
    id: 'Glossary.showPlayerInfo',
    defaultMessage: 'Show player info',
  },
  shuffle: { id: 'Glossary.shuffle', defaultMessage: 'Shuffle' },
  signedJerseys: {
    id: 'TutorialPage.jersey',
    defaultMessage: 'Signed jerseys',
  },
  signin: {
    id: 'ConnectionDialog.signin',
    defaultMessage: 'Sign in',
  },
  signup: {
    id: 'ConnectionDialog.signup',
    defaultMessage: 'Sign up',
  },
  skip: {
    id: 'glossary.skip',
    defaultMessage: 'Skip',
  },
  social: {
    id: 'Glossary.social',
    defaultMessage: 'Social',
  },
  soldBy: {
    id: 'CardOverview.soldBy',
    defaultMessage: 'Sold by',
  },
  squads: {
    id: 'Glossary.squads',
    defaultMessage: 'Squads',
  },
  starUniqueCountdown: {
    id: 'starUnique.countdown',
    defaultMessage:
      '<segment>{days}<unit>{days, plural, one {Day} other {Days}}</unit></segment><segment>{hours}<unit>{hours, plural, one {Hour} other {Hours}}</unit></segment><segment>{minutes}<unit>Min</unit></segment><segment>{seconds}<unit>Sec</unit></segment>',
  },
  start: {
    id: 'Glossary.start',
    defaultMessage: 'Start',
  },
  startCollecting: {
    id: 'Glossary.startCollecting',
    defaultMessage: 'Start collecting',
  },
  startingAt: {
    id: 'BuyField.startingAt',
    defaultMessage: 'Starting at',
  },
  startingSoon: {
    id: 'GameStatus.startingSoon',
    defaultMessage: 'Starting soon',
  },
  stats: {
    id: 'Glossary.stats',
    defaultMessage: 'Stats',
  },
  step: {
    id: 'Glossary.step',
    defaultMessage: 'Step',
  },
  streaks: {
    id: 'missions.GameWeekly.streaks',
    defaultMessage: 'Streaks',
  },
  submit: {
    id: 'Prompt2faDialog.submit',
    defaultMessage: 'Submit',
  },
  submitLineup: {
    id: 'LeaderboardGroup.EmptyState.CTA',
    defaultMessage: 'Submit lineup',
  },
  swapNow: {
    id: 'Glossary.swapNow',
    defaultMessage: 'Swap Now',
  },
  termsApply: {
    id: 'ConversionCreditTinyBanner.termsLink',
    defaultMessage: 'Terms apply',
  },
  ticket: { id: 'Glossary.Ticket', defaultMessage: 'Ticket' },
  tickets: { id: 'Glossary.Tickets', defaultMessage: 'Tickets' },
  tier: {
    id: 'CardReward.body.tier',
    defaultMessage: 'Tier',
  },
  today: {
    id: 'Glossary.Today',
    defaultMessage: 'Today',
  },
  tomorrow: {
    id: 'Glossary.tomorrow',
    defaultMessage: 'Tomorrow',
  },
  topPlayers: {
    id: 'PlayerRankings.title',
    defaultMessage: 'Top players',
  },
  trade: {
    id: 'USSportGalleryHeader.trade',
    defaultMessage: 'Trade',
  },
  transactions: {
    id: 'Glossary.transactions',
    defaultMessage: 'Transactions',
  },
  transferInProgress: {
    id: 'ItemSold.transferInProgress',
    defaultMessage: 'Transfer in progress',
  },
  twofa: {
    id: 'Settings.disable2FA.dialogTitle',
    defaultMessage: 'Two-factor Authentication',
  },
  unclaimed: { id: 'Glossary.Unclaimed', defaultMessage: 'Unclaimed' },
  undo: {
    id: 'Glossary.actions.undo',
    defaultMessage: 'Undo',
  },
  unfollow: {
    id: 'Follower.unfollow',
    defaultMessage: 'Unfollow',
  },
  unlock: {
    id: 'Glossary.unlock',
    defaultMessage: 'Unlock',
  },
  unlocked: {
    id: 'Glossary.unlocked',
    defaultMessage: 'Unlocked',
  },
  update: {
    id: 'Settings.updateProfile.submit',
    defaultMessage: 'Update',
  },
  updatedAt: {
    id: 'Glossary.updatedAt',
    defaultMessage: 'Updated',
  },
  upgrade: {
    id: 'Glossary.upgrade',
    defaultMessage: 'Upgrade',
  },
  upgraded: {
    id: 'Glossary.upgraded',
    defaultMessage: 'Upgraded',
  },
  verify: {
    id: 'Glossary.verify',
    defaultMessage: 'Verify',
  },
  viewAll: { id: 'MyNews.viewAll', defaultMessage: 'View All' },
  viewDetails: {
    id: 'Glossary.ViewLineupDetails',
    defaultMessage: 'View details',
  },
  viewGallery: {
    id: 'Glossary.viewGallery',
    defaultMessage: 'View gallery',
  },
  viewMore: { id: 'Glossary.ViewMore', defaultMessage: 'View More' },
  viewMyItems: {
    id: 'ClubShop.ItemPreviewDialog.Cta.ViewMyItems',
    defaultMessage: 'View My Items',
  },
  viewResults: {
    id: 'Glossary.ViewLineupResults',
    defaultMessage: 'View results',
  },
  viewed: {
    id: 'Glossary.viewed',
    defaultMessage: 'Viewed',
  },
  waitingForMatches: {
    id: 'Glossary.waitingForMatches',
    defaultMessage: 'Waiting for matches',
  },
  warning: {
    id: 'InventoryBoxesTypePage.Warning.title',
    defaultMessage: 'Warning',
  },
  week: {
    id: 'Glossary.week',
    defaultMessage: 'Week',
  },
  weekly: {
    id: 'Glossary.weekly',
    defaultMessage: 'Weekly',
  },
  wheel: {
    id: 'Glossary.wheel',
    defaultMessage: 'Sorare Wheel',
  },
  win: {
    id: 'Glossary.win',
    defaultMessage: 'Win',
  },
  withdraw: {
    id: 'bankEthAccounting.tabs.withdrawTo',
    defaultMessage: 'Withdraw',
  },
  xp: {
    id: 'FixtureOverview.xp',
    defaultMessage: 'XP',
  },
  yes: {
    id: 'WithdrawCancelDialog.confirmCta',
    defaultMessage: 'Yes',
  },
  you: {
    id: 'Glossary.you',
    defaultMessage: 'You',
  },
  zipCode: {
    id: 'ItemPreviewDialog.PostalAddressForm.zipCode',
    defaultMessage: 'Zip Code',
  },
});

export const tradeLabels = defineMessages({
  counterOfferWith: {
    id: 'Glossary.CounterOfferWith',
    defaultMessage: "Counter {nickname}'s trade",
  },
  tradeWith: {
    id: 'Glossary.TradeWith',
    defaultMessage: 'Trade with {nickname}',
  },
  usernameSends: {
    id: 'DirectOffer.usernameSends',
    defaultMessage: '<b>{username}</b> sends',
  },
  youReceive: {
    id: 'DealSummary.receiveTitle',
    defaultMessage: 'You receive',
  },
  youReceiveWithAvatar: {
    id: 'Glossary.youReceiveWithAvatar',
    defaultMessage: '<span>{nickname}</span> sends',
  },
  youSend: {
    id: 'DealSummary.sendTitle',
    defaultMessage: 'You send',
  },
});

export const emptyStateLabels = defineMessages({
  noLineupsEntered: {
    id: 'fixture.EmptyState.noLineupsEntered',
    defaultMessage: 'No Lineups Entered',
  },
});

export const navLabels = defineMessages({
  about: {
    id: 'navLabels.about',
    defaultMessage: 'About',
  },
  allCards: {
    id: 'navLabels.allCards',
    defaultMessage: 'All cards',
  },
  amateurPacks: {
    id: 'Nav.amateurPacks',
    defaultMessage: 'Amateur Packs',
  },
  apiTerms: {
    id: 'Nav.apiTerms',
    defaultMessage: 'API Terms',
  },
  arena: {
    id: 'Nav.arena',
    defaultMessage: 'Arena',
  },
  auctions: {
    id: 'navLabels.auctions',
    defaultMessage: 'Auctions',
  },
  blog: {
    id: 'Nav.blog',
    defaultMessage: 'Blog',
  },
  calendar: {
    id: 'Nav.play.calendar',
    defaultMessage: 'Calendar',
  },
  cardsSupply: {
    id: 'Seo.Player.CardsSupply.cardsSupply',
    defaultMessage: 'Cards supply',
  },
  careers: { id: 'Nav.careers', defaultMessage: 'Careers' },
  chase: {
    id: 'Nav.play.chase',
    defaultMessage: 'Chase',
  },
  clubHistory: {
    id: 'Nav.clubHistory',
    defaultMessage: 'Club History',
  },
  clubHonors: {
    id: 'Nav.ClubHonors',
    defaultMessage: 'Club Honors',
  },
  clubshop: {
    id: 'Nav.clubshop',
    defaultMessage: 'Club Shop',
  },
  collections: {
    id: 'Nav.collections',
    defaultMessage: 'Collections',
  },
  commonPacks: {
    id: 'Nav.commonPacks',
    defaultMessage: 'Common Packs',
  },
  competitions: {
    id: 'Nav.competitions',
    defaultMessage: 'Competitions',
  },
  contentCreatorTool: {
    id: 'Nav.contentCreatorTool',
    defaultMessage: 'Content Creator Tool',
  },
  cookiePolicy: {
    id: 'Nav.cookiePolicy',
    defaultMessage: 'Cookie Policy',
  },
  coverage: {
    id: 'Nav.coverage',
    defaultMessage: 'Coverage',
  },
  creatorProgram: {
    id: 'Nav.creatorProgram',
    defaultMessage: 'Creator Program',
  },
  faq: {
    id: 'Nav.faq',
    defaultMessage: 'FAQ',
  },
  fixtures: {
    id: 'Seo.Player.Header.Fixtures',
    defaultMessage: 'Fixtures',
  },
  footballFantasy: {
    id: 'Footer.footballFantasy',
    defaultMessage: 'Fantasy Football',
  },
  footballMarket: {
    id: 'Footer.footballMarket',
    defaultMessage: 'Football Market',
  },
  gallery: {
    id: 'Nav.gallery',
    defaultMessage: 'Gallery',
  },
  gameRules: {
    id: 'Nav.gameRules',
    defaultMessage: 'Game Rules',
  },
  gamingArena: {
    id: 'Nav.gamingArena',
    defaultMessage: 'Play',
  },
  gamingCompetitions: {
    id: 'Nav.gamingCompetitions',
    defaultMessage: 'Competitions',
  },
  hallOfFame: {
    id: 'Nav.hallOfFame',
    defaultMessage: 'Hall of Fame',
  },
  help: { id: 'Nav.help', defaultMessage: 'Help' },
  helpCenter: { id: 'Nav.helpCenter', defaultMessage: 'Help Center' },
  history: {
    id: 'Nav.rivals.history',
    defaultMessage: 'History',
  },
  home: {
    id: 'Nav.Home',
    defaultMessage: 'Home',
  },
  howToPlay: {
    id: 'navLabels.howToPlay',
    defaultMessage: 'How to Play',
  },
  inviteFriends: {
    id: 'Nav.referralProgram',
    defaultMessage: 'Invite Friends',
  },
  leagues: {
    id: 'Nav.leagues',
    defaultMessage: 'Leagues',
  },
  legalNotice: {
    id: 'Nav.legalNotice',
    defaultMessage: 'Legal Notice',
  },
  licensedClubs: {
    id: 'Nav.licensedClubs',
    defaultMessage: 'Licensed Clubs',
  },
  licensedPartners: {
    id: 'Nav.licensedPartners',
    defaultMessage: 'Licensed Partners',
  },
  lineups: {
    id: 'Nav.rivals.lineups',
    defaultMessage: 'Lineups',
  },
  live: {
    id: 'Nav.live',
    defaultMessage: 'Live',
  },
  lobby: {
    id: 'Nav.lobby',
    defaultMessage: 'Lobby',
  },
  managerAssistant: {
    id: 'Nav.managerAssistant',
    defaultMessage: 'Manager Assistant',
  },
  market: {
    id: 'Nav.market',
    defaultMessage: 'Market',
  },
  marketplaceTerms: {
    id: 'Nav.marketplaceTerms',
    defaultMessage: 'Marketplace Terms',
  },
  matches: {
    id: 'Nav.rivals.matches',
    defaultMessage: 'Matches',
  },
  matchups: {
    id: 'Nav.rivals.matchups',
    defaultMessage: 'Matchups',
  },
  members: {
    id: 'Nav.members',
    defaultMessage: 'Members',
  },
  mercato: {
    id: 'Nav.mercato',
    defaultMessage: 'Mercato',
  },
  missions: {
    id: 'Nav.missions',
    defaultMessage: 'Missions',
  },
  mlbFantasy: {
    id: 'Footer.mlbFantasy',
    defaultMessage: 'Fantasy MLB',
  },
  mlbMarket: {
    id: 'Footer.mlbMarket',
    defaultMessage: 'MLB Market',
  },
  mlbSignUp: {
    id: 'Nav.mlbSignUp',
    defaultMessage: 'MLB: Sign up',
  },
  mobileDebugScreen: {
    id: 'Nav.debugScreen',
    defaultMessage: 'Debug Screen',
  },
  more: {
    id: 'Nav.more',
    defaultMessage: 'More',
  },
  myAuctions: {
    id: 'Nav.myAuctions',
    defaultMessage: 'My Auctions',
  },
  myCards: {
    id: 'Nav.myCards',
    defaultMessage: 'My Cards',
  },
  myClub: {
    id: 'Nav.myClub',
    defaultMessage: 'My Club',
  },
  myFollows: {
    id: 'Nav.myFollows',
    defaultMessage: 'Follows',
  },
  myGallery: {
    id: 'Nav.myGallery',
    defaultMessage: 'My Cards',
  },
  myInstantBuys: {
    id: 'Nav.myInstantBuys',
    defaultMessage: 'My Instant Buys',
  },
  myLineups: {
    id: 'Nav.rivals.myLineups',
    defaultMessage: 'My Lineups',
  },
  myListings: {
    id: 'Nav.myListings',
    defaultMessage: 'My Listings',
  },
  myMarketActivity: {
    id: 'Nav.myMarketActivity',
    defaultMessage: 'My Market Activity',
  },
  myMatches: {
    id: 'Nav.myMatches',
    defaultMessage: 'My Matches',
  },
  myOffersReceived: {
    id: 'Nav.myOffersReceived',
    defaultMessage: 'Offers Received',
  },
  myOffersSent: {
    id: 'Nav.myOffersSent',
    defaultMessage: 'Offers Sent',
  },
  myPlayerNotifications: {
    id: 'Nav.myPlayerNotifications',
    defaultMessage: 'Player Notifications',
  },
  myProLineups: {
    id: 'Nav.myProLineups',
    defaultMessage: 'My Pro Lineups',
  },
  myPurchases: {
    id: 'Nav.myPurchases',
    defaultMessage: 'Manager Sales',
  },
  mySorare: {
    id: 'Nav.mySorare',
    defaultMessage: 'My Sorare',
  },
  mySquad: {
    id: 'Nav.mySquad',
    defaultMessage: 'My Squad',
  },
  myTransactions: {
    id: 'Nav.myTransactions',
    defaultMessage: 'Transactions',
  },
  nbaFantasy: {
    id: 'Footer.nbaFantasy',
    defaultMessage: 'Fantasy NBA',
  },
  nbaMarket: {
    id: 'Footer.nbaMarket',
    defaultMessage: 'NBA Market',
  },
  new: {
    id: 'Nav.new',
    defaultMessage: "What's New",
  },
  newSeason: {
    id: 'Nav.newSeason',
    defaultMessage: 'New Season',
  },
  notifications: {
    id: 'Notification.Notifications.notifications',
    defaultMessage: 'Notifications',
  },
  overview: {
    id: 'Seo.Player.Header.Overview',
    defaultMessage: 'Overview',
  },
  packs: {
    id: 'Nav.packs',
    defaultMessage: 'Packs',
  },
  play: {
    id: 'Nav.play',
    defaultMessage: 'Play',
  },
  players: {
    id: 'CollectionNavigationSwitch.players',
    defaultMessage: 'Players',
  },
  playoffs: {
    id: 'Nav.playoffs',
    defaultMessage: 'Playoffs',
  },
  press: {
    id: 'Nav.press',
    defaultMessage: 'Press',
  },
  privacyPolicy: {
    id: 'Nav.privacyPolicy',
    defaultMessage: 'Privacy Policy',
  },
  privateLeague: {
    id: 'Nav.privateLeague',
    defaultMessage: 'Private League',
  },
  privateLeagues: {
    id: 'Nav.privateLeagues',
    defaultMessage: 'Private Leagues',
  },
  prizePool: {
    id: 'navLabels.prizePool',
    defaultMessage: 'Prize Pool',
  },
  pro: {
    id: 'Nav.pro',
    defaultMessage: 'Pro',
  },
  profile: {
    id: 'Nav.profile',
    defaultMessage: 'Profile',
  },
  purchases: {
    id: 'Nav.purchases',
    defaultMessage: 'Manager Sales',
  },
  salesHistory: {
    id: 'Nav.salesHistory',
    defaultMessage: 'Sales history',
  },
  schedule: {
    id: 'Nav.play.schedule',
    defaultMessage: 'Schedule',
  },
  scores: {
    id: 'Nav.scores',
    defaultMessage: 'Scores',
  },
  search: {
    id: 'Nav.search',
    defaultMessage: 'Search',
  },
  set: {
    id: 'Nav.set',
    defaultMessage: 'Set',
  },
  settings: {
    id: 'Nav.settings',
    defaultMessage: 'Settings',
  },
  shop: {
    id: 'Nav.shop',
    defaultMessage: 'Shop',
  },
  standings: {
    id: 'Seo.Player.Header.Standings',
    defaultMessage: 'Standings',
  },
  stats: {
    id: 'Nav.stats',
    defaultMessage: 'Stats',
  },
  team: {
    id: 'Seo.Player.Header.Squad',
    defaultMessage: 'Players',
  },
  terms: {
    id: 'Nav.terms',
    defaultMessage: 'Terms & Conditions',
  },
  termsAndPrivacy: {
    id: 'Nav.termsAndPrivacy',
    defaultMessage: 'Terms & Privacy',
  },
  tradeMarket: {
    id: 'Nav.tradeMarket',
    defaultMessage: 'Trade Market',
  },
  transferMarket: {
    id: 'navLabels.market',
    defaultMessage: 'Market',
  },
  trending: {
    id: 'navLabels.trending',
    defaultMessage: 'Trending',
  },
  userClub: {
    id: 'Nav.userClub',
    defaultMessage: "{username}'s club",
  },
  vault: {
    id: 'Nav.vault',
    defaultMessage: 'Vault',
  },
});

export const galleryTabs = defineMessages({
  cardCollections: {
    id: 'Gallery.Data.cardCollections',
    defaultMessage: 'Collections',
  },
  cards: {
    id: 'Gallery.Data.cards',
    defaultMessage: 'Cards',
  },
});

export const userAttributes = defineMessages({
  clubName: {
    id: 'Global.user.clubName',
    defaultMessage: 'Club name',
  },
  currency: {
    id: 'Global.user.currency',
    defaultMessage: 'Conversion',
  },
  email: {
    id: 'Global.user.email',
    defaultMessage: 'Email',
  },
  fiatCurrency: {
    id: 'Global.user.fiatCurrency',
    defaultMessage: 'Currency',
  },
  nickname: {
    id: 'Users.nickname',
    defaultMessage: 'Username',
  },
  otpDisableAttempts: {
    id: 'Global.user.otpDisableAttempt',
    defaultMessage: 'Six-digit code OR recovery code',
  },
  password: {
    id: 'Users.password',
    defaultMessage: 'Password',
  },
  phoneNumber: {
    id: 'Global.user.phoneNumber',
    defaultMessage: 'Phone number',
  },
  status: {
    id: 'Global.user.status',
    defaultMessage: 'Status',
  },
});

export const fantasy = defineMessages({
  addCircle: {
    id: 'Fantasy.addToCircle',
    defaultMessage: 'Add to Circle',
  },
  addTeam: {
    id: 'AddTeam.newTeam',
    defaultMessage: 'Add Team',
  },
  addToList: {
    id: 'AddCardToDeck.addToList',
    defaultMessage: 'Add to list',
  },
  allAround: {
    id: 'DetailedScoresV4.allAround',
    defaultMessage: 'All around',
  },
  allAroundScore: {
    id: 'DetailedScoresV4.allAroundScore',
    defaultMessage: 'All around score',
  },
  allAroundShort: {
    id: 'fantasy.allAroundShort',
    defaultMessage: 'AA',
  },
  alreadyJoined: {
    id: 'LeaguePicker.alreadyJoined',
    defaultMessage: 'Already joined',
  },
  away: {
    id: 'Fantasy.away',
    defaultMessage: 'Away',
  },
  bonus: {
    id: 'Fantasy.bonus',
    defaultMessage: 'Bonus',
  },
  bonuses: {
    id: 'Fantasy.bonuses',
    defaultMessage: 'Bonuses',
  },
  boost: {
    id: 'CardPage.Level.Boosts',
    defaultMessage: 'Boost',
  },
  boost5x: {
    id: 'Fantasy.boost5x',
    defaultMessage: '5x Boost',
  },
  boxOdds: {
    id: 'BoxOddsDialog.boxOdds',
    defaultMessage: 'Box Odds',
  },
  boxes: {
    id: 'Fantasy.boxes',
    defaultMessage: 'Boxes',
  },
  captain: {
    id: 'Fantasy.captain',
    defaultMessage: 'Captain',
  },
  captainDescription: {
    id: 'composeTeam.Field.captain.description',
    defaultMessage:
      'The player you select as captain will get a {bonus, number, percent} bonus to their score.',
  },
  cardBonus: {
    id: 'Rules.getCardBonusRule.title',
    defaultMessage: 'Card Bonus',
  },
  collectionMilestones: {
    id: 'MissionsPage.collection.title',
    defaultMessage: 'Collection milestones',
  },
  completionMilestones: {
    id: 'MissionsPage.completion.title',
    defaultMessage: 'Completion milestones',
  },
  composeLineup: {
    id: 'Draft.ComposeOnboarding.title',
    defaultMessage: 'Compose your lineup',
  },
  computed: {
    id: 'so5.waiting',
    defaultMessage: 'Waiting',
  },
  confirmDelete: {
    id: 'Lobby.Upcoming.ConfirmDelete',
    defaultMessage: 'Are you sure you want to delete your team?',
  },
  confirmYourLineup: {
    id: 'Fantasy.confirmLineup',
    defaultMessage: 'Confirm your lineup',
  },
  credits: {
    id: 'Settings.conversionCredit.title',
    defaultMessage: 'Credits',
  },
  cumulativeLeaderboard: {
    id: 'Fantasy.cumulativeLeaderboard',
    defaultMessage: 'Cumulative leaderboard',
  },
  decisive: {
    id: 'DetailedScoresV4.decisive',
    defaultMessage: 'Decisive',
  },
  decisiveScore: {
    id: 'DetailedScoresV4.decisiveScore',
    defaultMessage: 'Decisive score',
  },
  decisiveScoreShort: {
    id: 'fantasy.decisiveScoreShort',
    defaultMessage: 'DS',
  },
  defeat: {
    id: 'Fantasy.defeat',
    defaultMessage: 'Defeat',
  },
  discoverCompetitions: {
    id: 'play.pro.fixture.enter.discoverCompetitions',
    defaultMessage: 'Discover Competitions',
  },
  discoverNewCompetitions: {
    id: 'play.pro.fixture.enter.discoverNewCompetitions',
    defaultMessage: 'Discover new competitions',
  },
  division: {
    id: 'Leaderboard.DivisionSwitcher.divDivision',
    defaultMessage: 'Div. {division}',
  },
  draft: { id: 'Fantasy.Draft', defaultMessage: 'Draft' },
  draw: {
    id: 'GameOutcome.draw',
    defaultMessage: 'Draw',
  },
  editLineup: {
    id: 'rivals.lineup.edit',
    defaultMessage: 'Edit Lineup',
  },
  eligibleCards: {
    id: 'league.SeasonalCard.eligibleCards',
    defaultMessage: 'Eligible cards',
  },
  ended: {
    id: 'TimeLeft.ended',
    defaultMessage: 'Ended',
  },
  entered: {
    id: 'campaigns.NextAction.entered',
    defaultMessage: 'Entered',
  },
  essenceShort: {
    id: 'Fantasy.essenceShort',
    defaultMessage: 'Essence',
  },
  essencesShort: {
    id: 'Fantasy.essencesShort',
    defaultMessage: 'Essences',
  },
  extraDraft: { id: 'Fantasy.ExtraDraft', defaultMessage: 'Extra Draft' },
  final: { id: 'GameInfos.final', defaultMessage: 'Final' },
  freeToPlay: {
    id: 'Fantasy.FreeToPlay',
    defaultMessage: 'Free-to-play',
  },
  fullTime: { id: 'GameInfos.fulltime', defaultMessage: 'FT' },
  gainOnProjection: {
    id: 'Fantasy.gainOnProjection',
    defaultMessage: 'Gain on Proj.',
  },
  game: {
    id: 'CompetitionDetails.LineupStats.Game',
    defaultMessage: 'Game',
  },
  gameCenter: {
    id: 'fantasy.gameCenter',
    defaultMessage: 'Game Center',
  },
  gameMode: {
    id: 'Fantasy.gameMode',
    defaultMessage: 'Game mode',
  },
  gameWeek: {
    id: 'Fantasy.gameWeek',
    defaultMessage: 'Game Week {gameWeek}',
  },
  gameplay: { id: 'Fantasy.Gameplay', defaultMessage: 'Gameplay' },
  gw: {
    id: 'Fantasy.gameweekShort',
    defaultMessage: 'GW {week}',
  },
  hallOfFame: {
    id: 'Fantasy.hallOfFame',
    defaultMessage: 'Hall of Fame',
  },
  hideTeam: {
    id: 'lineup.LineupsButtons.hideTeam',
    defaultMessage: 'Hide Team',
  },
  home: {
    id: 'Fantasy.home',
    defaultMessage: 'Home',
  },
  hotStreak: {
    id: 'Fantasy.hotStreak',
    defaultMessage: 'Hot Streak',
  },
  hotStreaks: {
    id: 'StreaksPromotion.hotStreaks',
    defaultMessage: 'Hot Streaks',
  },
  inArena: {
    id: 'Fantasy.inArena',
    defaultMessage: 'In Arena',
  },
  inCircle: {
    id: 'Fantasy.inMyCircle',
    defaultMessage: 'In My Circle',
  },
  incomplete: {
    id: 'LineupStatus.incomplete',
    defaultMessage: 'Incomplete',
  },
  joined: {
    id: 'rivals.joined',
    defaultMessage: 'Joined',
  },
  l10: {
    id: 'FixtureTopPlayersTableHeader.l10',
    defaultMessage: 'L10',
  },
  lastScores: {
    id: 'PlayerLastScores.title',
    defaultMessage: 'Last scores',
  },
  leaderboard: {
    id: 'Fantasy.leaderboard',
    defaultMessage: 'Leaderboard',
  },
  levelNumber: {
    id: 'Fantasy.level',
    defaultMessage: 'LVL {level}',
  },
  levelUp: {
    id: 'Fantasy.levelUp',
    defaultMessage: 'Level Up',
  },
  lineups: {
    id: 'Fantasy.Lineups',
    defaultMessage: 'Lineups',
  },
  lineupsCount: {
    id: 'Fantasy.LineupsCount',
    defaultMessage: '{lineupsCount, plural, one {1 Lineup} other {# Lineups}}',
  },
  lineupsRelativeCount: {
    id: 'Fantasy.LineupsRelativeCount',
    defaultMessage:
      '{lineupsCount}/{teamsCap, plural, one {1 Lineup} other {# Lineups}}',
  },
  listedCard: {
    id: 'leaderboard.card.listedCard',
    defaultMessage: 'Listed on Market',
  },
  live: {
    id: 'so5.live',
    defaultMessage: 'Live',
  },
  loss: {
    id: 'GameOutcome.loss',
    defaultMessage: 'Loss',
  },
  matchCenter: {
    id: 'fantasy.matchCenter',
    defaultMessage: 'Match Center',
  },
  missions: {
    id: 'missions.label.missions',
    defaultMessage: 'Missions',
  },
  nameLeaderboard: {
    id: 'fantasy.nameLeaderboard',
    defaultMessage: '{displayName} Leaderboard',
  },
  notEntered: {
    id: 'campaigns.NextAction.notEntered',
    defaultMessage: 'Not Entered',
  },
  numberOfmatches: {
    id: 'fantasy.numberOfMatches',
    defaultMessage: '{nbMatches, plural, one {# Match} other {# Matches}}',
  },
  objectives: {
    id: 'WarmupObjectives.title',
    defaultMessage: 'Objectives',
  },
  ordinalPoints: {
    id: 'Fantasy.ordinalPointsWithoutFormat',
    defaultMessage: '{score}<sup>pts</sup>',
  },
  participants: {
    id: 'Leaderboard.LineupsCount',
    defaultMessage:
      '{lineupsCount, plural, one {# participant} other {# participants}}',
  },
  past: {
    id: 'so5.past',
    defaultMessage: 'Past',
  },
  pastCompetitions: {
    id: 'Fantasy.pastCompetitions',
    defaultMessage: 'Past Competitions',
  },
  percentagePlayed: {
    id: 'Seo.Player.Infos.percentagePlayed',
    defaultMessage: '{percent, number, percent} played',
  },
  pickName: {
    id: 'CardSwap.CardSelection.title',
    defaultMessage: 'Pick {name}',
  },
  playerPerformance: {
    id: 'LastGamesPerformanceDialog.title',
    defaultMessage: 'Player performance',
  },
  playerScore: {
    id: 'Fantasy.playerScore',
    defaultMessage: 'Player Score',
  },
  playersPermittedFrom: {
    id: 'Fantasy.playersPermittedFrom',
    defaultMessage: 'Players permitted from',
  },
  podiumRank: {
    id: 'Lobby.CompetitionDetails.Rewards.Podium.rank',
    defaultMessage:
      '{rank, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} Place',
  },
  points: {
    id: 'Lobby.CompetitionDetails.Leaderboard.Score',
    defaultMessage: '{score, number, ::.00 rounding-mode-down} pts',
  },
  pointsCap: {
    id: 'Fantasy.pointsCap',
    defaultMessage: '{value, number} points cap',
  },
  pointsWithStyledOneDecimalScore: {
    id: 'Fantasy.pointsWithStyledOneDecimalScore',
    defaultMessage: '<span>{score, number, ::.0 rounding-mode-down}</span> pts',
  },
  pointsWithStyledScore: {
    id: 'Fantasy.points',
    defaultMessage:
      '<span>{score, number, ::.00 rounding-mode-down}</span> pts',
  },
  pointsWithStyledScoreWithoutFormat: {
    id: 'Fantasy.WithStyledScoreWithoutFormat',
    defaultMessage: '<span>{score}</span> pts',
  },
  pointsWithoutFormat: {
    id: 'Fantasy.pointsWithoutFormat',
    defaultMessage: '{score} pts',
  },
  prizePool: {
    id: 'CompetitionDetails.TabsItem.rewards',
    defaultMessage: 'Prize pool',
  },
  prizes: {
    id: 'CompetitionDetails.Prizes',
    defaultMessage: 'Prizes',
  },
  proHistory: {
    id: 'Fantasy.proHistory',
    defaultMessage: 'Pro History',
  },
  projectedRewards: {
    id: 'CompetitionDetails.Rewards.projectedRewards',
    defaultMessage: 'Projected Rewards',
  },
  projectedScore: {
    id: 'Fantasy.projectedScore',
    defaultMessage: 'Projected Score',
  },
  projectedScoreShort: {
    id: 'Fantasy.projectedScoreShort',
    defaultMessage: 'Proj. Score',
  },
  promotion: {
    id: 'LeaderboardOverview.promotion',
    defaultMessage: 'Promotion',
  },
  rank: {
    id: 'Fantasy.rank',
    defaultMessage: 'Rank',
  },
  ranked: {
    id: 'Fantasy.ranked',
    defaultMessage: 'Ranked',
  },
  reachTargetPoints: {
    id: 'composeTeam.Thresholds.target',
    defaultMessage: 'Reach {target} points',
  },
  redraft: { id: 'Fantasy.Redraft', defaultMessage: 'Redraft' },
  referralReward: {
    id: 'ClaimReferralReward.title',
    defaultMessage: 'Referral Reward',
  },
  registerLineup: {
    id: 'Fantasy.RegisterLineup',
    defaultMessage: 'Register Lineup',
  },
  registerNewTeam: {
    id: 'AddTeam.registerNewTeam',
    defaultMessage: 'Register a new team',
  },
  relegation: {
    id: 'LeaderboardOverview.relegation',
    defaultMessage: 'Relegation',
  },
  removeCard: {
    id: 'leaderboard.card.removeCard',
    defaultMessage: 'Playing this card would remove it from the Market',
  },
  removeCircle: {
    id: 'Fantasy.removeFromCircle',
    defaultMessage: 'Remove from Circle',
  },
  replay: {
    id: 'Fantasy.replay',
    defaultMessage: 'Replay',
  },
  reveal: {
    id: 'Fantasy.reveal',
    defaultMessage: 'Reveal',
  },
  revealCards: {
    id: 'Fantasy.revealCards',
    defaultMessage: 'Reveal Cards',
  },
  rewardsBoost: {
    id: 'Fantasy.rewardsBoost',
    defaultMessage: 'Rewards boost',
  },
  rewardsBoostPct: {
    id: 'Fantasy.rewardsBoostPct',
    defaultMessage: '+{pct} rewards',
  },
  rewardsPerRank: {
    id: 'LazyRewards.rewardsPerRank',
    defaultMessage: 'Rewards per rank',
  },
  rewardsPerTarget: {
    id: 'LazyRewards.rewardsPerTarget',
    defaultMessage: 'Rewards per target',
  },
  rewardsPerThreshold: {
    id: 'LazyRewards.rewardsPerThreshold',
    defaultMessage: 'Rewards per threshold',
  },
  room: {
    id: 'Leaderboard.DivisionSwitcher.room',
    defaultMessage: 'Room {division}',
  },
  rules: {
    id: 'Fantasy.rules',
    defaultMessage: 'Rules',
  },
  safeZone: {
    id: 'Fantasy.safeZone',
    defaultMessage: 'Safe zone',
  },
  scarcities: {
    id: 'BenchFilter.scarcities',
    defaultMessage: 'Scarcities',
  },
  score: {
    id: 'Fantasy.score',
    defaultMessage: 'Score',
  },
  scoutPopularPlayer: {
    id: 'BasicBench.scoutPlayer',
    defaultMessage: 'Scout player',
  },
  scoutPopularPosition: {
    id: 'BasicBench.scoutPosition',
    defaultMessage: 'Scout {position}',
  },
  scrap: {
    id: 'Fantasy.scrap',
    defaultMessage: 'Scrap',
  },
  seal: {
    id: 'fantasy.seal',
    defaultMessage: 'Seal',
  },
  selectPlayer: {
    id: 'rivals.draft.CaptainSelection.cta',
    defaultMessage: 'Select player',
  },
  selectYourPlayer: {
    id: 'composeTeam.Field.SelectYourPlayer',
    defaultMessage: 'Select your player',
  },
  selectYourPosition: {
    id: 'composeTeam.Field.SelectYourPosition',
    defaultMessage: 'Select your {position}',
  },
  setLineup: {
    id: 'lineup.LineupsButtons.setLineups',
    defaultMessage: 'Set Lineup',
  },
  shortCumulativeLeaderboard: {
    id: 'Fantasy.shortCumulativeLeaderboard',
    defaultMessage: 'Cumulative',
  },
  startSealing: {
    id: 'Collections.sealing.skeleton.btn.startSealing',
    defaultMessage: 'Start Sealing',
  },
  suggest: {
    id: 'lineup.LineupButtons.suggest',
    defaultMessage: 'Suggest',
  },
  suggestLineup: {
    id: 'lineup.LineupButtons.suggestLineup',
    defaultMessage: 'Suggest Lineup',
  },
  swap: { id: 'Fantasy.Swap', defaultMessage: 'Swap' },
  target: {
    id: 'Fantasy.target',
    defaultMessage: 'Target',
  },
  targetNumber: {
    id: 'ThresholdRewards.target',
    defaultMessage: 'Target {targetNb}',
  },
  targetPoints: {
    id: 'fantasy.targetPoints',
    defaultMessage: '{target, number}+ points',
  },
  team: {
    id: 'Fantasy.team',
    defaultMessage: 'Team',
  },
  teamVersus: {
    id: 'Fantasy.teamVersus',
    defaultMessage: '{homeTeam} vs {awayTeam}',
  },
  total: {
    id: 'Fantasy.total',
    defaultMessage: 'Total',
  },
  totalPointsAboveCap: {
    id: 'Glossary.Compose.totalPointsAboveCap',
    defaultMessage: 'Total points above cap',
  },
  totalPrizePool: {
    id: 'Fantasy.totalPrizePool',
    defaultMessage: 'Total Prize Pool',
  },
  trainingTeams: {
    id: 'Fantasy.trainingTeams',
    defaultMessage: 'Training Teams',
  },
  undoNumber: {
    id: 'Fantasy.undoNumber',
    defaultMessage: 'Undo <span>({value, number})</span>',
  },
  upcoming: {
    id: 'so5.upcoming',
    defaultMessage: 'Upcoming',
  },
  victory: {
    id: 'Fantasy.victory',
    defaultMessage: 'Victory',
  },
  viewAllMatches: {
    id: 'play.pro.fixture.enter.seasonality.competition.trck.viewAllMatches',
    defaultMessage: 'View All Matches',
  },
  viewLeaderboard: {
    id: 'rivals.Arena.RankProgression.viewLeaderboard',
    defaultMessage: 'View Leaderboard',
  },
  viewLineup: {
    id: 'lineup.LineupsButtons.viewLineup',
    defaultMessage: 'View Lineup',
  },
  viewPrizePool: {
    id: 'SeasonLeaderboardHeader.viewPrizePool',
    defaultMessage: 'View Prize Pool',
  },
  viewRewards: {
    id: 'Fantasy.viewRewards',
    defaultMessage: 'View Rewards',
  },
  viewRewardsPool: {
    id: 'Fantasy.viewRewardsPool',
    defaultMessage: 'View Rewards Pool',
  },
  viewSchedule: {
    id: 'Fantasy.viewSchedule',
    defaultMessage: 'View Schedule',
  },
  vs: {
    id: 'Fantasy.vs',
    defaultMessage: 'vs',
  },
  win: {
    id: 'GameOutcome.win',
    defaultMessage: 'Win',
  },
  yourLineups: {
    id: 'Fantasy.yourLineups',
    defaultMessage: 'Your Lineups',
  },
  yourTeam: {
    id: 'Fantasy.yourTeam',
    defaultMessage: 'Your team',
  },
});

export const cardAttributes = defineMessages({
  L10: {
    id: 'CardSuggestion.averageScore.l10Short',
    defaultMessage: 'Last 10 Games',
  },
  L10Score: {
    id: 'AverageScore.lastTenPlayedSo5AverageScore',
    defaultMessage: 'Average score over the last 10 played games',
  },
  L15: {
    id: 'CardSuggestion.averageScore.l15Short',
    defaultMessage: 'Last 15 Games',
  },
  L15DecisiveScore: {
    id: 'AverageScore.lastFifteenDecisiveSo5AverageScore',
    defaultMessage: 'Average decisive score over the last 15 games',
  },
  L15Score: {
    id: 'AverageScore.lastFifteenSo5AverageScore',
    defaultMessage: 'Average score over the last 15 games',
  },
  L40Score: {
    id: 'AverageScore.lastFortyAverageScore',
    defaultMessage: 'Average score over the last 40 games',
  },
  L5Score: {
    id: 'AverageScore.lastFiveAverageScore',
    defaultMessage: 'Average score over the last 5 games',
  },
  age: { id: 'Cards.age', defaultMessage: 'Age' },
  averageScore: { id: 'Cards.averageScore', defaultMessage: 'Average score' },
  blockchainID: { id: 'Cards.blockchainID', defaultMessage: 'Blockchain ID' },
  bonus: { id: 'Cards.bonus', defaultMessage: 'Bonus' },
  cardEdition: {
    id: 'RefineList.cardEdition',
    defaultMessage: 'Card edition',
  },
  cardTeam: { id: 'RefineList.cardTeam', defaultMessage: 'Card Team' },
  club: { id: 'Cards.club', defaultMessage: 'Club' },
  collectibles: {
    id: 'glossary.title',
    defaultMessage: 'Collectibles',
  },
  country: { id: 'Cards.country', defaultMessage: 'Country' },
  eligibility: {
    id: 'Glossary.CardAttributes.eligibility',
    defaultMessage: 'Eligibility',
  },
  height: { id: 'Glossary.CardAttributes.height', defaultMessage: 'Height' },
  jerseyNumber: {
    id: 'Glossary.CardAttributes.jerseyNumber',
    defaultMessage: 'Jersey Number',
  },
  last10: {
    id: 'Nba.Seo.Players.Slug.Overview.last10',
    defaultMessage: 'Last 10',
  },
  last15: {
    id: 'Glossary.CardAttributes.last15',
    defaultMessage: 'Last 15',
  },
  last40: {
    id: 'Glossary.CardAttributes.last40',
    defaultMessage: 'Last 40',
  },
  last5: {
    id: 'Glossary.CardAttributes.last5',
    defaultMessage: 'Last 5',
  },
  level: { id: 'Cards.level', defaultMessage: 'Level' },
  player: { id: 'RefineList.player', defaultMessage: 'Player' },
  position: { id: 'RefineList.position', defaultMessage: 'Position' },
  scarcity: { id: 'Cards.scarcity', defaultMessage: 'Scarcity' },
  sealTitle: { id: 'Cards.sealTitle', defaultMessage: 'Seal Value' },
  season: { id: 'Cards.season', defaultMessage: 'Season' },
  seasonAverageScore: {
    id: 'CardProperties.averageScore.season',
    defaultMessage: 'Season average score',
  },
  serialNumber: { id: 'Cards.serialNumber', defaultMessage: 'Serial Number' },
  serie: { id: 'Cards.serial', defaultMessage: 'Serial' },
  shortLevel: { id: 'Cards.shortLevel', defaultMessage: 'lvl' },
  team: { id: 'RefineList.team', defaultMessage: 'Team' },
  unsealTitle: { id: 'Cards.unsealTitle', defaultMessage: 'Unseal Value' },
  upcomingGame: {
    id: 'Cards.upcomingGame',
    defaultMessage: 'This player has an upcoming game.',
  },
  usedInLineup: {
    id: 'sellCommonCard.used',
    defaultMessage: 'Used in a lineup',
  },
  xpTitle: {
    id: 'Cards.xpTitle',
    defaultMessage: 'Total XP',
  },
  xpValue: {
    id: 'Cards.xpValue',
    defaultMessage: '{xp} XP',
  },
});

export const filters = defineMessages({
  activeClub: {
    id: 'RefineList.activeClub',
    defaultMessage: 'Current Club',
  },
  activeNationalTeam: {
    id: 'RefineList.activeNationalTeam',
    defaultMessage: 'Current National Team',
  },
  activeTeam: {
    id: 'RefineList.activeTeam',
    defaultMessage: 'Current Team',
  },
  appearances: {
    id: 'RangeSlider.appearances',
    defaultMessage: 'Games Played',
  },
  averageScore: {
    id: 'RangeSlider.averageScore',
    defaultMessage: 'Average Score',
  },
  bidToEarnLabel: {
    id: 'RefineList.bidToEarn',
    defaultMessage: 'Bid to earn',
  },
  birthplace: {
    id: 'RefineList.birthplace',
    defaultMessage: 'Birthplace',
  },
  bundledSale: {
    id: 'RefineList.bundledSale',
    defaultMessage: 'Sale type',
  },
  cardLevel: {
    id: 'RangeSlider',
    defaultMessage: 'Card Level',
  },
  clearAll: {
    id: 'ClearAllFilters.cta',
    defaultMessage: 'Clear all filters',
  },
  collectibleFilterTitle: {
    id: 'CollectibleFilter.collectible',
    defaultMessage: 'Collectible',
  },
  competitionEligibilityFilterTitle: {
    id: 'LeagueFilter.competitionEligibility',
    defaultMessage: 'League & Competition',
  },
  creditEligibleFilter: {
    id: 'RefineCreditEligible.label',
    defaultMessage: 'Credit eligible',
  },
  customDeck: {
    id: 'RefineList.customDeck',
    defaultMessage: 'Squad',
  },
  customList: {
    id: 'RefineList.customList',
    defaultMessage: 'List',
  },
  dailyFilterLabel: {
    id: 'DailyFilter.label',
    defaultMessage: 'Playing Game Day',
  },
  duplicate: {
    id: 'DuplicateFilter.label',
    defaultMessage: 'Duplicate',
  },
  favoriteFilterLabel: {
    id: 'FavoriteFilter.label',
    defaultMessage: 'Favorites',
  },
  filters: {
    id: 'Activity.filterDialogTitle',
    defaultMessage: 'Filters',
  },
  floorPrices: {
    id: 'floorPrices.label',
    defaultMessage: 'Price',
  },
  gainOnClassicProjection: {
    id: 'RangeSlider.projectedScoreDifference',
    defaultMessage: 'Next Game Week Gain',
  },
  gainOnDailyProjection: {
    id: 'RangeSlider.projectedScoreDifferenceDaily',
    defaultMessage: 'Next Daily Gain',
  },
  gameweekFilterLabel: {
    id: 'gameweekFilter.label',
    defaultMessage: 'Playing Game Week',
  },
  jerseySerialLabel: {
    id: 'JerseySerialFilter.label',
    defaultMessage: 'Matching player jersey number',
  },
  lastFifteenAppearances: {
    id: 'RangeSlider.lastFifteenAppearences',
    defaultMessage: 'Last 15 Appearances',
  },
  lastFifteenAverageScore: {
    id: 'RangeSlider.lastFifteenAverageScore',
    defaultMessage: 'Last 15 Average Score',
  },
  lastFiveAppearances: {
    id: 'RangeSlider.lastFiveAppearances',
    defaultMessage: 'Last 5 Appearances',
  },
  lastFiveAverageScore: {
    id: 'RangeSlider.lastFiveAverageScore',
    defaultMessage: 'Last 5 Average Score',
  },
  latestSeasonLabel: {
    id: 'RefineLatestSeason.label',
    defaultMessage: 'Latest Season',
  },
  legendFilterLabel: {
    id: 'CollectibleFilter.legendFilterLabel',
    defaultMessage: 'Show Legend Cards',
  },
  mlbEligibleLeaderboards: {
    id: 'RefineList.eligible_leaderboards',
    defaultMessage: 'Eligible Leaderboards',
  },
  mlbLastFifteenAverageScore: {
    id: 'RefineLastFifteenAverageScore.last_fifteen_average_score',
    defaultMessage: 'Last 15 average score',
  },
  mlbSeasonAverageScore: {
    id: 'RefineSeasonAverageScore.season_average_score',
    defaultMessage: 'Season average score',
  },
  nationality: {
    id: 'RefineList.nationality',
    defaultMessage: 'Nationality',
  },
  nbaShortTenGameAverageScore: {
    id: 'RefineTenGameAverageScore.short.ten_game_average_score',
    defaultMessage: 'L10 Avg.',
  },
  nbaTenGameAverageScore: {
    id: 'RefineTenGameAverageScore.ten_game_average_score',
    defaultMessage: 'Ten Game Average',
  },
  noCardsFound: {
    id: 'EmptyGallery.noCardFound',
    defaultMessage: 'No Cards found',
  },
  noCardsOfTopicFound: {
    id: 'EmptyGallery.noCardsOfTopicFound',
    defaultMessage: 'No Cards of {topic} found',
  },
  nonPlayableCardsFilterLabel: {
    id: 'CollectibleFilter.showCustomSeriesCards',
    defaultMessage: 'Show Custom Series Cards',
  },
  notInLineupFilterLabel: {
    id: 'NotInLineupFilter.label',
    defaultMessage: 'Not in lineup',
  },
  onSaleFilterLabel: {
    id: 'RefineOnSale.label',
    defaultMessage: 'Cards on sale',
  },
  playingStatusFilterLabel: {
    id: 'PlayingStatusFilter.label',
    defaultMessage: 'Playing status',
  },
  price: {
    id: 'RangeSlider.price',
    defaultMessage: 'Price',
  },
  probablePitchersFilterLabel: {
    id: 'probablePitchersFilter.label',
    defaultMessage: 'Probable Pitchers',
  },
  projectedScoreClassic: {
    id: 'RangeSlider.projectedScore',
    defaultMessage: 'Next Game Week projection',
  },
  projectedScoreDaily: {
    id: 'RangeSlider.projectedScoreDaily',
    defaultMessage: 'Next Daily projection',
  },
  promotedCardsFilterTitle: {
    id: 'PromotedCards.title',
    defaultMessage: 'Special Event',
  },
  promotionalEvent: {
    id: 'RefineList.promotionalEvent',
    defaultMessage: 'Promotional event',
  },
  saleType: {
    id: 'RefineList.saleType',
    defaultMessage: 'Sale type',
  },
  settlementWallets: {
    id: 'RefineList.settlementWallets',
    defaultMessage: 'Purchase options',
  },
  showDetailsLabel: {
    id: 'ShowDetails.label',
    defaultMessage: 'Show details',
  },
  showTrainingLineups: {
    id: 'ShowTrainingLineups.label',
    defaultMessage: 'Show training lineups',
  },
  sortBy: {
    id: 'FiltersManagerDialog.sortBy',
    defaultMessage: 'Sort by',
  },
  startedOnlyFilterLabel: {
    id: 'StartedOnlyFilter.label',
    defaultMessage: 'Not empty',
  },
  tournament: {
    id: 'RefineList.tournament',
    defaultMessage: 'Tournament',
  },
  watchlist: {
    id: 'WatchlistFilter.label',
    defaultMessage: 'Watchlists',
  },
});

/* eslint-disable sort-keys */
export const sorts = defineMessages<
  AlgoliaCardIndexesName | PrintableI18nPlayerSortName | GallerySortName
>({
  'Best Value': {
    id: 'SortCard.bestValue',
    defaultMessage: 'Most Relevant',
  },
  'Cards Highest Average Score': {
    id: 'SortCard.highestAverageScore',
    defaultMessage: 'Highest Average Score',
  },
  'Cards Highest Price': {
    id: 'SortCard.highestPrice',
    defaultMessage: 'Highest Price',
  },
  'Cards Lowest Price': {
    id: 'SortCard.lowestPrice',
    defaultMessage: 'Lowest Price',
  },
  'Sealing Value': {
    id: 'SortCard.sealValue',
    defaultMessage: 'Sealing Value',
  },
  New: {
    id: 'SortCards.new',
    defaultMessage: 'New',
  },
  'Cards New': {
    id: 'SortCards.cardsNew',
    defaultMessage: 'New',
  },
  'Cards On Sale Best Value': {
    id: 'SortCard.bestValue',
    defaultMessage: 'Most Relevant',
  },
  'Cards On Sale Ending Soon': {
    id: 'SortCard.endingSoon',
    defaultMessage: 'Ending Soon',
  },
  'Cards On Sale Highest Average Score': {
    id: 'SortCard.highestAverageScore',
    defaultMessage: 'Highest Average Score',
  },
  'Cards On Sale Highest Price': {
    id: 'SortCard.highestPrice',
    defaultMessage: 'Highest Price',
  },
  'Cards On Sale Lowest Price': {
    id: 'SortCard.lowestPrice',
    defaultMessage: 'Lowest Price',
  },
  'Cards On Sale Newly Listed': {
    id: 'SortCard.newlyListed',
    defaultMessage: 'Newly listed',
  },
  'Cards On Sale Popular Player': {
    id: 'SortCard.popularPlayer',
    defaultMessage: 'Popular Player',
  },
  'Cards On Sale Bids Count': {
    id: 'SortCard.bidsCount',
    defaultMessage: 'Bids Count',
  },
  'Cards Player Name': {
    id: 'SortCard.playerName',
    defaultMessage: 'Player Name',
  },
  'Ending Soon': {
    id: 'SortCard.endingSoon',
    defaultMessage: 'Ending Soon',
  },
  'Highest Average Score': {
    id: 'SortCard.highestAverageScore',
    defaultMessage: 'Highest Average Score',
  },
  'Highest Price': {
    id: 'SortCard.highestPrice',
    defaultMessage: 'Highest Price',
  },
  'Lowest Price': {
    id: 'SortCard.lowestPrice',
    defaultMessage: 'Lowest Price',
  },
  'Newly Listed': {
    id: 'SortCard.newlyListed',
    defaultMessage: 'Newly listed',
  },
  'Popular Player': {
    id: 'SortCard.popularPlayer',
    defaultMessage: 'Popular Player',
  },
  'DESC-L10': {
    id: 'SortCard.last_ten_played_so5_average_score-DESC',
    defaultMessage: 'Highest Average Score',
  },
  'DESC-L15': {
    id: 'SortCard.last_fifteen_played_so5_average_score-DESC',
    defaultMessage: 'Highest Average Score',
  },
  'ASC-player_name': {
    id: 'SortCard.display_name-ASC',
    defaultMessage: 'Player Name (A-Z)',
  },
  'DESC-player_name': {
    id: 'SortCard.display_name-DESC',
    defaultMessage: 'Player Name (Z-A)',
  },
  'ASC-name_for_sorting': {
    id: 'SortCard.display_name-ASC',
    defaultMessage: 'Player Name (A-Z)',
  },
  'DESC-name_for_sorting': {
    id: 'SortCard.display_name-DESC',
    defaultMessage: 'Player Name (Z-A)',
  },
  Tier: {
    id: 'SortCard.gameplay_tier-DESC',
    defaultMessage: 'Tier',
  },
});
/* eslint-enable sort-keys */

export const playerUnavailability = defineMessages({
  active: {
    id: 'Status.player.active',
    defaultMessage: 'Active',
  },
  expectedReturnDate: {
    id: 'Card.unavailability.expectedReturnDate',
    defaultMessage: 'Expected return date: {date, date, long}',
  },
  gamesUnavailableCount: {
    id: 'Card.suspension.gamesUnavailable',
    defaultMessage:
      '({gamesCount, plural, one {1 {competition} game} other {# {competition} games}})',
  },
  inactive: {
    id: 'Status.player.inactive',
    defaultMessage: 'Inactive',
  },
  injuryTitle: {
    id: 'Draft.Card.injured',
    defaultMessage: 'Potential injury',
  },
  suspendedTitle: {
    id: 'Draft.Card.suspended',
    defaultMessage: 'Suspended',
  },
  unavailableSince: {
    id: 'Card.unavailabilty.unavailableSinceDate',
    defaultMessage: '(since {unavailableSince, date, long})',
  },
  unknownReturnDate: {
    id: 'Card.unavailability.unknownReturnDate',
    defaultMessage: 'Unknown return date',
  },
});

export const playerGameStatusLabels = defineMessages({
  did_not_play: {
    id: 'So5Stat.did_not_play',
    defaultMessage: 'Did not play',
  },
  did_not_play_short: {
    id: 'So5Stat.did_not_play_short',
    defaultMessage: 'DNP',
  },
  low_coverage: {
    id: 'So5Stat.low_coverage',
    defaultMessage: 'Low Coverage',
  },
  low_coverage_detail: {
    id: 'So5Stat.low_coverage_detail',
    defaultMessage:
      'Only Decisive Actions (eg goals, assists) will be scored for this game.',
  },
  no_game: {
    id: 'So5Stat.no_game',
    defaultMessage: 'No game (NG)',
  },
  no_game_short: {
    id: 'So5Stat.no_game_short',
    defaultMessage: 'NG',
  },
  not_covered: {
    id: 'So5Stat.not_covered',
    defaultMessage: 'Not Covered',
  },
  pending: {
    id: 'PlayerScore.pending',
    defaultMessage: 'Match not yet started',
  },
  reviewing: {
    id: 'PlayerGame.waitingForReview',
    defaultMessage: 'Score waiting for review',
  },
  uncertain_coverage: {
    id: 'So5Stat.uncertain_coverage',
    defaultMessage: 'Delayed Coverage',
  },
  uncertain_coverage_detail: {
    id: 'So5Stat.uncertain_coverage_detail',
    defaultMessage:
      'Full player data (All-Around scores + Decisive Scores) for this game will not be provided in real-time. It should update within 48 hours after full-time. However, if full player stats are not provided by our data partner before we finalise scoring at the end of the Game Week (targeted at 8pm CET at the latest), only decisive stats will be scored.',
  },
});

export const playerDetails = defineMessages({
  birthDateAndAge: {
    id: 'Seo.Player.Info.birthDateAndAge',
    defaultMessage: 'Date of birth (Age)',
  },
  breakdown: {
    id: 'PlayerDetails.breakdown',
    defaultMessage: 'Breakdown',
  },
  drawn: {
    id: 'Seo.Club.Overview.drawn',
    defaultMessage: 'D',
  },
  gamesCount: {
    id: 'Lineup.player.gamesCount',
    defaultMessage: '{count, plural, one {# game} other {# games}}',
  },
  gamesLeftCount: {
    id: 'Lineup.player.gamesLeftCount',
    defaultMessage: '{count, plural, one {# game left} other {# games left}}',
  },
  goalsAgainst: {
    id: 'Seo.Club.Overview.goalsAgainst',
    defaultMessage: 'GA',
  },
  goalsDiff: {
    id: 'Seo.Club.Overview.goalsDiff',
    defaultMessage: 'GF',
  },
  goalsFor: {
    id: 'Seo.Club.Overview.goalsFor',
    defaultMessage: 'GF',
  },
  injuryReport: {
    id: 'InfoModal.injuryReport',
    defaultMessage: 'Injury report',
  },
  leagueRanking: {
    id: 'PlayerDetails.leagueRanking',
    defaultMessage: 'League ranking',
  },
  lost: {
    id: 'Seo.Club.Overview.lost',
    defaultMessage: 'L',
  },
  noGames: {
    id: 'CompetitionDetails.LineupStats.NoGames',
    defaultMessage: 'No games',
  },
  notStarting: {
    id: 'StartingStatus.notStarting2',
    defaultMessage: 'Not Starting',
  },
  performance: {
    id: 'InfoModal.performance',
    defaultMessage: 'Performance',
  },
  played: {
    id: 'Seo.Club.Overview.played',
    defaultMessage: 'PL',
  },
  player_status: {
    id: 'InfoModal.playerStatus',
    defaultMessage: 'Player status',
  },
  points: {
    id: 'Seo.Club.Overview.points',
    defaultMessage: 'PTS',
  },
  starting: {
    id: 'StartingStatus.starting',
    defaultMessage: 'Starting',
  },
  upcomingMatches: {
    id: 'PlayerDetails.upcomingMatches',
    defaultMessage: 'Upcoming matches',
  },
  upcoming_games: {
    id: 'InfoModal.upcomingGames',
    defaultMessage: 'Upcoming games',
  },
  won: {
    id: 'Seo.Club.Overview.won',
    defaultMessage: 'W',
  },
});

export const mobileApp = defineMessages({
  disabledSale: {
    id: 'mobileApp.disabledSale',
    defaultMessage: 'Sell (disabled in app)',
  },
  disabledTrade: {
    id: 'mobileApp.disabledTrade',
    defaultMessage: 'Trade (disabled in app)',
  },
  openWebMarketplace: {
    id: 'mobileApp.openWebMarketplace',
    defaultMessage: 'Visit Market',
  },
  startTrading: {
    id: 'Offers.cannotTrade.mobileApp',
    defaultMessage: 'To start trading, visit Sorare on web',
  },
  unavailable: {
    id: 'mobileApp.unavailable',
    defaultMessage: 'Unavailable',
  },
});

export const wallet = defineMessages({
  addFunds: {
    id: 'bankEthAccounting.tabs.addFunds',
    defaultMessage: 'Add funds',
  },
  disconnect: {
    id: 'DepositEthForm.disconnect',
    defaultMessage: 'Disconnect from Wallet',
  },
  emptyWallet: {
    id: 'DepositEthForm.emptyWallet',
    defaultMessage:
      'Please transfer funds to your external wallet: it is currently empty.',
  },
  signTransaction: {
    id: 'DepositEthForm.signTransaction',
    defaultMessage: 'Please sign the transaction with your wallet.',
  },
  sixDigitsCode: {
    id: 'Global.user.sixDigitCode',
    defaultMessage: 'Six-digit code (Two-factor Authentication)',
  },
  walletIsLockedTitle: {
    id: 'NewWalletDrawer.walletIsLockedTitle',
    defaultMessage: 'Unlock your wallet',
  },
});

export const payment = defineMessages({
  applePay: {
    id: 'Glossary.applePay',
    defaultMessage: 'Apple Pay',
  },
  balance: {
    id: 'PaymentBox.balance',
    defaultMessage: 'Balance',
  },
  confirmAndPay: {
    id: 'Glossary.paymentBox.confirmAndPay',
    defaultMessage: 'Confirm and Pay now',
  },
  confirmationTimeout: {
    id: 'Glossary.confirmationTimeout',
    defaultMessage:
      'Payment is taking longer then expected to be processed, please retry later.',
  },
  conversionCreditDefinition: {
    id: 'ConversionCredits.tooltipV4',
    defaultMessage:
      'Credits apply to a percentage of your card(s) purchase, up to a specified limit. Credits are automatically applied using the maximum eligible amount available, with the closest expiration date used first.',
  },
  googlePay: {
    id: 'Glossary.googlePay',
    defaultMessage: 'Google Pay',
  },
  linkCo: {
    id: 'Glossary.LinkCo',
    defaultMessage: 'Link',
  },
  payment: {
    id: 'Glossary.payment',
    defaultMessage: 'Payment',
  },
  paymentBoxTitle: {
    id: 'Glossary.paymentBox.title',
    defaultMessage: 'Review & Pay',
  },
  peggingDescription: {
    id: 'Settings.receivedPaymentCurrencies.pegging.description',
    defaultMessage:
      'The price of the cards you list for sale will be tied to {cryptocurrencySymbol}.',
  },
  peggingTitle: {
    id: 'Settings.receivedPaymentCurrencies.pegging.title',
    defaultMessage: 'Peg card prices to {cryptocurrencySymbol}',
  },
  seller: {
    id: 'Glossary.seller',
    defaultMessage: 'Seller',
  },
  sorareCashWallet: {
    id: 'Glossary.sorareCashWallet',
    defaultMessage: 'Cash Wallet',
  },
  sorareCryptoWallet: {
    id: 'Glossary.sorareCryptoWallet',
    defaultMessage: '{cryptocurrencySymbol} Wallet',
  },
  sorareWallet: {
    id: 'Glossary.sorareWallet',
    defaultMessage: 'Sorare Wallet',
  },
  wallet: {
    id: 'Glossary.wallet',
    defaultMessage: 'Wallet',
  },
});

export const transferMarket = defineMessages({
  bundle: {
    id: 'Bundle.bundle',
    defaultMessage: 'Bundle',
  },
  bundles: {
    id: 'TransferMarket.bundles',
    defaultMessage: 'Bundles',
  },
  buyMore: {
    id: 'buyField.buyMore',
    defaultMessage: 'Buy more',
  },
  buySellAndTradeCards: {
    id: 'TransferMarket.buySellAndTradeCards',
    defaultMessage: 'Buy, sell and trade cards',
  },
  directOffer: {
    id: 'TransferMarket.directOffer',
    defaultMessage: 'Direct offer',
  },
  favorites: {
    id: 'TransferMarket.favorites',
    defaultMessage: 'My Favorites',
  },
  instantBuy: {
    id: 'PrimaryBuyDetails.instantBuy',
    defaultMessage: 'Instant Buy',
  },
  new: {
    id: 'TransferMarket.primarySales',
    defaultMessage: 'New Card Auctions',
  },
  orderSummary: {
    id: 'buyField.tokenSummary.title',
    defaultMessage: 'Order Summary',
  },
  priceGraph: {
    id: 'Seo.Player.PriceGraph.priceGraph',
    defaultMessage: 'Price graph',
  },
  publicOffer: {
    id: 'PriceHistory.publicOffer',
    defaultMessage: 'Public offer',
  },
  singleCards: {
    id: 'TransferMarket.singleCards',
    defaultMessage: 'Single cards',
  },
  starterPacks: {
    id: 'TransferMarket.starterPacks',
    defaultMessage: 'Starter Packs',
  },

  transfer: {
    id: 'TransferMarket.userSales',
    defaultMessage: 'Manager Sales',
  },
  transferDescription: {
    id: 'TransferMarket.userSalesDescription',
    defaultMessage: 'Buy, sell, and trade cards with other Managers.',
  },
});

export const fiatWallet = defineMessages({
  activateCashWallet: {
    id: 'FiatWallet.activateCashWallet',
    defaultMessage: 'Activate my Cash Wallet',
  },
  activateCashWalletAndAddAnId: {
    id: 'FiatWallet.activateCashWalletAndAddAnId',
    defaultMessage: 'Activate Cash Wallet and add an ID',
  },
  addCash: { id: 'FiatWallet.addCash', defaultMessage: 'Add cash' },
  addMyId: {
    id: 'FiatWallet.addMyId',
    defaultMessage: 'Add my ID',
  },
});

export const gameStatusMessages: {
  [key in GameStatus | string]: MessageDescriptor;
} = defineMessages({
  canceled: { id: 'GameStatus.canceled', defaultMessage: 'Canceled' },
  cancelled: { id: 'GameStatus.cancelled', defaultMessage: 'Canceled' },
  delayed: { id: 'GameStatus.delayed', defaultMessage: 'Delayed' },
  live: { id: 'Game.status.live', defaultMessage: 'Live' },
  played: { id: 'GameStatus.final', defaultMessage: 'Full Time' },
  playing: { id: 'GameStatus.playing', defaultMessage: 'Playing' },
  postponed: { id: 'GameStatus.postponed', defaultMessage: 'Postponed' },
  scheduled: { id: 'GameStatus.scheduled', defaultMessage: 'Scheduled' },
  suspended: { id: 'GameStatus.suspended', defaultMessage: 'Suspended' },
});

export const shopCategories: { [key in ShopItemType]?: MessageDescriptor } =
  defineMessages({
    [ShopItemType.BANNER]: {
      id: 'ShopItemDialog.Title.Banner',
      defaultMessage: 'My Banners',
    },
    [ShopItemType.SHIELD]: {
      id: 'ShopItemDialog.Title.Shield',
      defaultMessage: 'My Badges',
    },
    [ShopItemType.BACKGROUND]: {
      id: 'ShopItemDialog.Title.Background',
      defaultMessage: 'My stadiums',
    },
    [ShopItemType.CLUB_BADGE]: {
      id: 'ShopItemDialog.Title.ClubBadge',
      defaultMessage: 'My Club Badges',
    },
    [ShopItemType.ACHIEVEMENT_BADGE]: {
      id: 'ShopItemDialog.Title.AchievementBadge',
      defaultMessage: 'My Achievement Badges',
    },
    [ShopItemType.EXTRA_SWAP]: {
      id: 'ShopItemDialog.Title.ExtraSwap',
      defaultMessage: 'My Extra Swap',
    },
    [ShopItemType.EXTRA_TEAMS_CAP]: {
      id: 'ShopItemDialog.Title.ExtraTeamsCap',
      defaultMessage: 'My Extra Teams',
    },
    [ShopItemType.LOGO]: {
      id: 'ShopItemDialog.Title.Logo',
      defaultMessage: 'My Logos',
    },
    [ShopItemType.LEVEL_UP]: {
      id: 'ShopItemDialog.Title.LevelUp',
      defaultMessage: 'My level ups',
    },
    [ShopItemType.JERSEY]: {
      id: 'ShopItemDialog.Title.Jersey',
      defaultMessage: 'My Jerseys',
    },
    [ShopItemType.DELIVERABLE_WITH_NO_VARIANT]: {
      id: 'ShopItemDialog.Title.DeliverableWithNoVariant',
      defaultMessage: 'My Caps',
    },
  });

export const seasonEligibilityMessages: {
  [key in SeasonEligibility]: MessageDescriptor;
} = defineMessages({
  [SeasonEligibility.IN_SEASON]: {
    id: 'seasonEligibility.inSeason',
    defaultMessage: 'In Season',
  },
  [SeasonEligibility.CLASSIC]: {
    id: 'seasonEligibility.classic',
    defaultMessage: 'Classic',
  },
});

export const referralStateMessages = defineMessages<ReferralState>({
  [ReferralState.ALL]: {
    id: 'MyReferees.all',
    defaultMessage: 'All states',
  },
  [ReferralState.COMPLETED]: {
    id: 'MyReferees.completed',
    defaultMessage: 'Successful',
  },
  [ReferralState.EXPIRED]: {
    id: 'MyReferees.expired',
    defaultMessage: 'Expired',
  },
  [ReferralState.IN_PROGRESS]: {
    id: 'MyReferees.inProgress',
    defaultMessage: 'In progress',
  },
  [ReferralState.PENDING_REVIEW]: {
    id: 'MyReferees.pendingReview',
    defaultMessage: 'Pending review',
  },
  [ReferralState.PENDING_REFEREE_CONFIRMATION]: {
    id: 'MyReferees.pendingRefereeConfirmation',
    defaultMessage: 'Pending confirmation',
  },
  [ReferralState.BLOCKED]: {
    id: 'MyReferees.blocked',
    defaultMessage: 'Suspended',
  },
});

export const inventoryMessages = defineMessages({
  allTeams: {
    id: 'Inventory.RevealableCards.allTeams',
    defaultMessage: 'All teams',
  },
  noRevealableCards: {
    id: 'Inventory.RevealableCards.noRevealableCards',
    defaultMessage: 'No cards to reveal',
  },
  noRevealableMatchingCards: {
    id: 'Inventory.RevealableCards.noRevealableMatchingCards',
    defaultMessage: 'No revealable cards matching "{query}"',
  },
  revealAll: {
    id: 'Inventory.RevealableCards.search.reveal_all',
    defaultMessage: 'Reveal all cards',
  },
  revealAllConfirmTitle: {
    id: 'Inventory.RevealableCards.revealAll.confirm.title',
    defaultMessage: 'Are you sure?',
  },
  searchPlaceholder: {
    id: 'Inventory.RevealableCards.searchPlaceholder',
    defaultMessage: 'Search by player, team, position...',
  },
});

export const invoiceTableMessages = defineMessages({
  fees: { id: 'Withdrawal.fees', defaultMessage: 'Fees' },
  subtotal: { id: 'SummaryTable.subtotal', defaultMessage: 'Subtotal' },
});

export const commonMarketplace = defineMessages({
  addNotAvailable: {
    id: 'SwapHeader.switch.addNotAvailable',
    defaultMessage: 'Not available',
  },
  drop: {
    id: 'SwapHeader.switch.drop',
    defaultMessage: 'Drop',
  },
  dropNotAvailable: {
    id: 'SwapHeader.switch.dropNotAvailable',
    defaultMessage: 'Not available',
  },
});

export const scoutingCenter = defineMessages({
  advancedView: {
    id: 'toggleStatsManager.cta',
    defaultMessage: 'Advanced view',
  },
  decisiveActions: {
    id: 'searchCommonPlayer.statsManager.stats.decisiveActions.title',
    defaultMessage: 'Decisive actions',
  },
  findTheBestPlayers: {
    id: 'ForYou.ScoutingCenter.title',
    defaultMessage: 'Find the best players',
  },
  last15Total: {
    id: 'statsDisplayToggle.last15Total',
    defaultMessage: 'Last 15 total',
  },
  myWatchlists: {
    id: 'ScoutingCenter.myWatchlists',
    defaultMessage: 'My Watchlists',
  },
  per90: {
    id: 'statsDisplayToggle.per90',
    defaultMessage: 'Per 90’',
  },
  perGame: {
    id: 'statsDisplayToggle.perMatch',
    defaultMessage: 'Per game',
  },
  projectionClassic: {
    id: 'statsManager.projection.so5FixtureEvent.classic',
    defaultMessage: 'Next Game Week projection',
  },
  projectionClassicTooltip: {
    id: 'projection.tooltip.classic',
    defaultMessage: 'Next Game Week projection {rotowireLogo}',
  },
  projectionDaily: {
    id: 'statsManager.projection.so5FixtureEvent.daily',
    defaultMessage: 'Next Daily projection',
  },
  projectionDailyTooltip: {
    id: 'projection.tooltipMessages.daily',
    defaultMessage: 'Next Daily projection {rotowireLogo}',
  },
  projectionDifferenceClassic: {
    id: 'statsManager.projection.difference.classic',
    defaultMessage:
      'Difference between Game Week projection and L10 Average Score',
  },
  projectionDifferenceDaily: {
    id: 'statsManager.projection.difference.daily',
    defaultMessage: 'Difference between daily projection and L10 Average Score',
  },
  projectionGrade: {
    id: 'AdvancedBenchObjectTableHeader.PG',
    defaultMessage: 'PG',
  },
  projectionGradeClassic: {
    id: 'statsManager.projection.so5FixtureEvent.classicGrade',
    defaultMessage: 'Next Game Week projection grade',
  },
  projectionGradeClassicTooltip: {
    id: 'projection.tooltipMessages.classicGrade',
    defaultMessage: 'Next Game Week projection grade',
  },
  projectionGradeDaily: {
    id: 'statsManager.projection.so5FixtureEvent.dailyGrade',
    defaultMessage: 'Next Daily projection grade',
  },
  projectionGradeDailyTooltip: {
    id: 'projection.tooltipMessages.dailyGrade',
    defaultMessage: 'Next Daily projection grade',
  },
  scout: {
    id: 'ForYou.ScoutingCenter.CTA',
    defaultMessage: 'Scout',
  },
  scoutNow: {
    id: 'ScoutingCenter.scoutNow',
    defaultMessage: 'Scout Now',
  },
  scoutingCenter: {
    id: 'ForYou.ScoutingCenter.label',
    defaultMessage: 'Scouting Center',
  },
  statistics: {
    id: 'searchCommonPlayer.statsManager.stats.football.title',
    defaultMessage: 'Statistics',
  },
  stats: {
    id: 'LastScores.stats',
    defaultMessage: 'Stats',
  },
  watchlists: {
    id: 'ScoutingCenter.watchlists',
    defaultMessage: 'Watchlists',
  },
});

export const cart = defineMessages({
  add: {
    id: 'Cart.add',
    defaultMessage: 'Add',
  },
  addAllToCart: {
    id: 'Cart.addAllToCart',
    defaultMessage: 'Add all to cart',
  },
  addToCart: {
    id: 'Cart.addToCart',
    defaultMessage: 'Add to cart',
  },
  addToLineup: {
    id: 'Cart.addToLineup',
    defaultMessage: 'Add to lineup',
  },
  addedToCart: {
    id: 'Cart.addedToCart',
    defaultMessage: 'Added to cart',
  },
  cart: {
    id: 'Cart.cart',
    defaultMessage: 'Cart',
  },
  checkout: {
    id: 'Cart.checkout',
    defaultMessage: 'Checkout',
  },
  clearAll: {
    id: 'Cart.clearAll',
    defaultMessage: 'Clear all',
  },
  creditApplied: {
    id: 'Cart.creditApplied',
    defaultMessage: 'Credit applied',
  },
  creditAvailable: {
    id: 'Cart.creditAvailable',
    defaultMessage: 'Credit available',
  },
  inCart: {
    id: 'CartItem.inCart',
    defaultMessage: 'In Cart',
  },
  saveForLater: {
    id: 'Cart.saveForLater',
    defaultMessage: 'Save for later',
  },
  savedForLater: {
    id: 'Cart.savedForLater',
    defaultMessage: 'Saved for later',
  },
  soldOut: {
    id: 'Cart.soldOut',
    defaultMessage: 'Sold out',
  },
  total: {
    id: 'Cart.total',
    defaultMessage: 'Total',
  },
});

export const marketCredits = defineMessages({
  description: {
    id: 'ConversionCreditReward.description',
    defaultMessage:
      '{percentage, number, percent} discount up to {maxDiscount} off',
  },
  haveDiscountCredits: {
    id: 'ConversionCreditBanner.haveDiscountCredits',
    defaultMessage:
      'Your {percentageDiscount, number, percent} market credits (up to { maxDiscount }) is expiring in just {timeLeft}! Take a peek <walletLink>in your wallet</walletLink> to view all your available credits.',
  },
  marketCredits: {
    id: 'ConversionCreditReward.marketCredits',
    defaultMessage: 'Market credits',
  },
});

export const congratsScreens = defineMessages({
  newSigning: {
    id: 'buyTokenConfirmation.buy.title',
    defaultMessage:
      'New{br}{cardsCount, plural, =1 {signing} other {signings}}',
  },
  welcomeMultiplePlayersToYourTeam: {
    id: 'buyTokenConfirmation.buy.subtitle.multiple',
    defaultMessage:
      'Welcome to the Squad! {cardsCount} New Players Now Part of Your Team!',
  },
  welcomePlayerToYourTeam: {
    id: 'buyTokenConfirmation.buy.subtitle',
    defaultMessage: 'Welcome {playerDisplayName} to your team!',
  },
});

export const onboarding = defineMessages({
  discount: {
    id: 'onboarding.PromoButton',
    defaultMessage: 'Get {percentageDiscount, number, percent} off',
  },
  expert: {
    id: 'UnlockPro.veteranTitle2',
    defaultMessage: 'Expert',
  },
  expertDescription: {
    id: 'UnlockPro.veteranDescription',
    defaultMessage: 'Skip onboarding and go to the Market',
  },
});

export const wheel = defineMessages({
  bestFiveClue: {
    id: 'wheel.clue.fifty_fifty_craft_clue',
    defaultMessage: 'BEST 5 CLUE',
  },
  bestStarRankClue: {
    id: 'wheel.clue.best_star_rank_craft_clue',
    defaultMessage: 'BEST STAR RANK CLUE',
  },
  competitionClue: {
    id: 'wheel.clue.competition_craft_clue',
    defaultMessage: 'COMPETITION CLUE',
  },
  countryClue: {
    id: 'wheel.clue.country_craft_clue',
    defaultMessage: 'COUNTRY CLUE',
  },
  divisionClue: {
    id: 'wheel.clue.division_craft_clue',
    defaultMessage: 'DIVISION CLUE',
  },
  gems: {
    id: 'wheel.slot.gem',
    defaultMessage: 'Gems',
  },
  howToWin: {
    id: 'marketplace.wheel.tickets.howToWin',
    defaultMessage: 'How to win tickets?',
  },
  nothing: {
    id: 'wheel.slot.miss',
    defaultMessage: 'Miss!',
  },
  positionClue: {
    id: 'wheel.clue.position_craft_clue',
    defaultMessage: 'POSITION CLUE',
  },
  potionClue: {
    id: 'wheel.clue.potion_craft_clue',
    defaultMessage: 'POTION CLUE',
  },
  shuffleClue: {
    id: 'wheel.clue.shuffle_craft_clue',
    defaultMessage: 'SNOWGLOBE SHUFFLE CLUE',
  },
  spin: {
    id: 'marketplace.wheel.spin',
    defaultMessage: 'Spin',
  },
  spinAgain: {
    id: 'wheel.slot.spinAgain',
    defaultMessage: 'Spin again',
  },
});

export const cardPull = defineMessages({
  availableCompetitions: {
    id: 'CardPullInfos.AvailableCompetitions.title',
    defaultMessage: 'Available competitions',
  },
  clues: {
    id: 'CardPullInfos.Clues.title',
    defaultMessage: 'Clues',
  },
  competitionClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.competitionClue',
    defaultMessage: 'Competition',
  },
  cost: {
    id: 'CardPull.HowItWorks.cluesCost.cost',
    defaultMessage: 'Cost x{cost}',
  },
  countryClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.countryClue',
    defaultMessage: 'Country',
  },
  divisionClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.divisionClue',
    defaultMessage: 'Division',
  },
  drawAndOdds: {
    id: 'cardPull.drawAndOdds',
    defaultMessage: 'Draw and odds',
  },
  fiftyFiftyClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.fiftyFiftyClue',
    defaultMessage: 'Best 5',
  },
  myEssence: {
    id: 'CardPullInfos.EssenceFlavours.title',
    defaultMessage: 'My Essence',
  },
  positionClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.positionClue',
    defaultMessage: 'Position',
  },
  potionClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.potionClue',
    defaultMessage: 'Potion',
  },
  rulesAndEligibility: {
    id: 'cardPull.rulesAndEligibility',
    defaultMessage: 'Rules & Eligibility',
  },
  shuffleClue: {
    id: 'buyTokenConfirmation.buy.inGameCurrencyReward.subtitle.shuffleClue',
    defaultMessage: 'Snowglobe Shuffle',
  },
});
